import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { FaChartLine } from "react-icons/fa";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import academicStyles from "../../../pages/schoolCockpit/master/academicConfiguration/Academiclist.module.css";
import dashStyles from "./AiDashboard.module.css";
import AxiosInstance, { TokenService } from "../../../services/Api";
import DailyAttendanceCard from "../../../components/Chart/DailyAttendanceCard/DailyAttendanceCard";
import CustomDonutChart from "../../../components/Chart/DrilldownChart/CustomDonutChart";
import LineChart from "../../../components/Chart/LineChart/LineChart";
import CustomBarChart from "../../../components/Chart/CustomBarChart";
import AcademicHealthInsights from "../../../components/Chart/AcademicHealthInsight/Academichealthinsights";
import SubjectEngagementInsights from "../../../components/Chart/Subjectengagementinsights/Subjectengagementinsights";
import ClassPerformanceHeatmap, {
  MarkCell,
} from "../../../components/Chart/Classperformanceheatmap/Classperformanceheatmap";
import SyllabusCompletion from "../../../components/Chart/Syllabuscompletion/Syllabuscompletion";
import ExamPerformanceChart from "../../../components/Chart/ExamWisePerformance/ExamWisePerformance";
import HorizontalBarChart from "../../../components/Chart/Horizontalbarchart/Horizontalbarchart";
import CustomLoader from "../../../components/CustomLoader/CustomLoader";
import LineAndBarChart from "../../../components/Chart/ComboChart/LineAndBarChart";
import CustomDropdown from "../../../components/CustomDropdown/CustomDropdown";

/* ─────────────────────────────────────────────────────────────────────────
 *  Constants & pure helpers
 * ───────────────────────────────────────────────────────────────────────── */
const ATTENDANCE_LINE_COLOR = "#38bdf8";
const FAIL_LINE_COLOR = "#fca5a5";
const REMEDIAL_LINE_COLOR = "#38bdf8"; // orange
const FTR_LINE_COLOR = "#b197fc"; // violet
const ATTENDANCE_THRESHOLD = 50;
const AHS_RISK_CODE = { noRisk: 0, mediumRisk: 1, highRisk: 2 };

/* Pass / Fail / Unattended stacked segments for the trackers */
const TRACKER_SEGMENTS = [
  { key: "pass", darkVar: "--green-dark", lightVar: "--green-light" },
  { key: "fail", darkVar: "--red-dark", lightVar: "--red-light" },
  { key: "unattended", darkVar: "--orange-dark", lightVar: "--orange-light" },
];
/* 3-char month names for the attendance-trend month filter labels, e.g. "Jan 2026". */
const MONTH_NAMES_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const getInitials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .map((w) => w[0] ?? "")
    .join("")
    .toUpperCase()
    .slice(0, 2);

const todayISO = () => new Date().toISOString().split("T")[0];

const formatPhone = (mobile) => (mobile ? `+91 ${mobile}` : "");

const isAbortError = (err) =>
  err?.name === "AbortError" ||
  err?.name === "CanceledError" ||
  err?.code === "ERR_CANCELED";

const mapClassSectionOptions = (data = []) => {
  const seen = new Set();
  return data.reduce((acc, item) => {
    const classStaff = Number(item.class_staff) === 1 ? 1 : 0;
    const subjectId = item.subject_id ? String(item.subject_id) : "";
    const key =
      classStaff === 1
        ? `${item.class_id}::${item.section_id}::ct`
        : `${item.class_id}::${item.section_id}::s::${subjectId}`;
    if (seen.has(key)) return acc;
    seen.add(key);

    const suffix =
      classStaff === 1 ? "Class Teacher" : item.subject_name || "Subject";

    acc.push({
      class_id: item.class_id,
      class_name: item.class_name,
      section_id: item.section_id,
      class_staff: classStaff,
      subject_id: subjectId || null,
      subject_name: item.subject_name || null,
      label: `${item.class_name} - ${item.section_id} - ${suffix}`,
      value: key,
    });
    return acc;
  }, []);
};

const computeYAxis = (values) => {
  const all = [...values, ATTENDANCE_THRESHOLD];
  const minVal = Math.min(...all);
  const maxVal = Math.max(...all);
  const yMin = Math.max(0, Math.floor((minVal - 5) / 5) * 5);
  const yMax = Math.min(100, Math.ceil((maxVal + 5) / 5) * 5);
  const range = yMax - yMin;
  const yStepSize = range <= 20 ? 2 : range <= 50 ? 5 : 10;
  return { yMin, yMax, yStepSize };
};

const mapAhsResponse = (raw) => {
  const apiData = raw?.data || {};
  const summary = apiData.summary || {};
  const students = apiData.student || [];
  return {
    summary: {
      total: summary.total_student || 0,
      noRisk: summary["less-risk"] || 0,
      mediumRisk: summary["medium-risk"] || 0,
      highRisk: summary["high-risk"] || 0,
    },
    students: students.map((s) => ({
      id: String(s.student_id),
      name: s.name,
      phone: formatPhone(s.mobile_no),
      overallExamScore: s.overall_exam_score ?? 0,
      attendancePercent: s.attendance_pct ?? 0,
      assessmentRemedialScore: s.assessment_remedial_pct ?? 0,
      applicationUsagePercent: s.application_usage_pct ?? 0,
      academicHealthScore:
        (s.academic_health_score || s.subject_health_score) ?? 0,
    })),
  };
};

/* Empty breakdown shape — keeps CustomDonutChart in BreakdownDonut mode
 * before the API resolves. A null data object would make the dispatcher
 * fall back to the single-arc attendance donut, so we seed zeros instead. */
const EMPTY_ACADEMIC_HEALTH = {
  compositeScore: 0,
  status: "",
  breakdown: {
    examMarks: { weight: 0, score: 0 },
    attendance: { weight: 0, score: 0 },
    assessmentRemedial: { weight: 0, score: 0 },
    applicationUsage: { weight: 0, score: 0 },
  },
  aiSummary: "",
};

/* ── Subject Academic Health Score (CustomDonutChart breakdown, subject teacher) ──
 * /dashboard/subjectAcademicHealthScore
 *
 * API shape:
 *   data.components[] = { name, key, weight, performance_pct, contribution }
 *   data.total_percentage = overall health as a percentage already
 *                          (e.g. 3.0571 means 3.06%)  ← NOT a 0–1 fraction
 *
 * Arc size + on-arc "40%" label → component.weight
 * Tooltip "Score: X/100"        → component.performance_pct
 * Tooltip "Contribution"        → derived in the chart as score*weight/100
 * Center composite score        → data.total_percentage (preserved to 1 decimal)
 *
 * Note: this endpoint differs from /dashboard/class_teacher/academic_healthscore
 * in that total_percentage is already a percentage value, not a 0–1 fraction.
 * That's why this mapper exists separately. */
const mapSubjectAcademicHealthScoreResponse = (raw) => {
  const d = raw?.data || {};
  const num = (v) => Number(v) || 0;

  // API component key → BreakdownDonut segment key (DEFAULT_SEGMENT_CONFIG)
  const KEY_MAP = {
    exam: "examMarks",
    attendance: "attendance",
    lapsed: "assessmentRemedial",
    session: "applicationUsage",
  };

  // Seed zeros so the donut keeps a stable arc order (and stays in
  // BreakdownDonut mode) even if the API omits a component.
  const breakdown = {
    examMarks: { weight: 0, score: 0 },
    attendance: { weight: 0, score: 0 },
    assessmentRemedial: { weight: 0, score: 0 },
    applicationUsage: { weight: 0, score: 0 },
  };

  const components = Array.isArray(d.components) ? d.components : [];
  components.forEach((c) => {
    const segKey = KEY_MAP[c.key];
    if (!segKey) return;
    breakdown[segKey] = {
      weight: num(c.weight), // arc size + on-arc weight label
      score: num(c.performance_pct), // tooltip "Score: X/100"
    };
  });

  // total_percentage is already a percentage (e.g. 3.0571 → 3.1%).
  // Preserve one decimal of precision so values like 3.06% don't read as "3".
  const compositeRaw = num(d.total_percentage);
  const composite = Math.round(compositeRaw * 10) / 10;

  const status = composite >= 70 ? "green" : composite >= 40 ? "orange" : "red";
  const overall = Math.round(num(d.total_percentage) * 100);

  return {
    compositeScore: composite,
    status,
    breakdown,
    aiSummary: "",
  };
};

/* ── Academic Health Score (CustomDonutChart breakdown, class teacher) ──
 * /dashboard/class_teacher/academic_healthscore
 *
 * New API shape:
 *   data.components[] = { name, key, weight, performance_pct, contribution }
 *   data.total_percentage = overall health as a 0–1 fraction (e.g. 0.1)
 *
 * Arc size + on-arc "40%" label → component.weight
 * Tooltip "Score: X/100"        → component.performance_pct
 * Tooltip "Contribution"        → derived in the chart as score*weight/100
 * Center composite score        → Σ contribution (≈ total_percentage * 100) */
const mapAcademicHealthScoreResponse = (raw) => {
  const d = raw?.data || {};
  const num = (v) => Number(v) || 0;

  // API component key → BreakdownDonut segment key (DEFAULT_SEGMENT_CONFIG)
  const KEY_MAP = {
    exam: "examMarks",
    attendance: "attendance",
    lapsed: "assessmentRemedial",
    session: "applicationUsage",
  };

  // Seed zeros so the donut keeps a stable arc order (and stays in
  // BreakdownDonut mode) even if the API omits a component.
  const breakdown = {
    examMarks: { weight: 0, score: 0 },
    attendance: { weight: 0, score: 0 },
    assessmentRemedial: { weight: 0, score: 0 },
    applicationUsage: { weight: 0, score: 0 },
  };

  const components = Array.isArray(d.components) ? d.components : [];
  let composite = 0;

  components.forEach((c) => {
    const segKey = KEY_MAP[c.key];
    if (!segKey) return;
    breakdown[segKey] = {
      weight: num(c.weight), // arc size + "40%" on-arc label
      score: num(c.performance_pct), // tooltip "Score: X/100"
    };
  });
  const overall = num(d.total_percentage);
  const status = overall >= 70 ? "green" : overall >= 40 ? "orange" : "red";

  return {
    compositeScore: overall,
    status,
    breakdown,
    aiSummary: "",
  };
};

const mapSubjectEngagementResponse = (raw) => {
  const payload = raw?.data || raw || {};

  // Class teacher → payload.subjects (array)
  // Subject teacher → payload.subject (singular object)
  const subjectsArray = Array.isArray(payload.subjects)
    ? payload.subjects
    : payload.subject
      ? [payload.subject]
      : [];

  const allStudents = payload.students || [];

  return {
    subjects: subjectsArray.map((subj) => ({
      // Prefer subject_id when present (subject teacher), fall back to name (class teacher)
      id: String(subj.subject_id ?? subj.subject),
      name: subj.subject,
      totalStudents: subj.total_students ?? 0,
      classAverage: subj.class_average ?? 0,
      summary: {
        noRisk: subj.no_risk ?? 0,
        mediumRisk: subj.medium_risk ?? 0,
        highRisk: subj.high_risk ?? 0,
      },
      // Match students by subject_id when present, otherwise by name
      students: allStudents
        .filter((s) =>
          subj.subject_id != null
            ? s.subject_id === subj.subject_id
            : s.subject === subj.subject,
        )
        .map((s) => ({
          id: String(s.student_id),
          name: s.name,
          phone: formatPhone(s.phone),
          engagementScore: s.engagement_score ?? 0,
          status: s.status,
        })),
    })),
  };
};

const mapSyllabusCompletionResponse = (raw) => {
  const classes = Array.isArray(raw?.data?.classes) ? raw.data.classes : [];
  const seen = new Set();
  const rows = [];

  classes.forEach((cls) => {
    (cls.subjects || []).forEach((s) => {
      const id = String(s.subject_id);
      if (seen.has(id)) return; // de-dupe if the API ever returns the subject twice
      seen.add(id);
      rows.push({
        id,
        subject: s.subject_name,
        staff: s.staff_name,
        overall: Number(s.overall_percentage) || 0,
        month: Number(s.month_percentage) || 0,
        // Overall side
        totalPages: Number(s.total_pages) || 0,
        overallCompletedPages: Number(s.overall_completed_pages) || 0,
        // Month side
        subTotalPages: Number(s.sub_total_pages) || 0,
        monthCompletedPages: Number(s.month_completed_pages) || 0,
      });
    });
  });

  return rows;
};

const mapSubjectSyllabusResponse = (raw) => {
  const d = raw?.data;
  if (!d || typeof d !== "object") return [];
  return [
    {
      id: String(d.subject_id ?? ""),
      subject: d.subject_name || "",
      staff: d.staff_name || "",
      overall: Number(d.overall_percentage) || 0,
      month: Number(d.month_percentage) || 0,
      totalPages: d.total_pages ?? 0,
      completedPages: d.pages_completed ?? 0,
    },
  ];
};

const mapHeatmapResponse = (raw) => {
  const apiData = raw?.data || raw || {};
  return {
    summary: {
      totalStudents: apiData.summary?.total_students ?? 0,
      studentsPassed: apiData.summary?.students_passed ?? 0,
      studentsFailed: apiData.summary?.students_failed ?? 0,
      passPercentage: apiData.summary?.pass_percentage ?? 0,
      failPercentage: apiData.summary?.fail_percentage ?? 0,
    },
    subjects: (apiData.subjects || []).map((s) => ({
      id: String(s.subject_id),
      name: s.subject_name,
    })),
    students: (apiData.students || []).map((s) => {
      const marks = {};
      (s.marks_details || []).forEach((m) => {
        marks[String(m.subject_id)] = m.marks;
      });
      return {
        id: String(s.student_id),
        name: s.student_name,
        marks,
        totalMark: s.total_mark ?? 0,
        rank: s.rank ?? 0,
      };
    }),
    pagination: {
      currentPage: apiData.pagination?.current_page ?? 1,
      perPage: apiData.pagination?.per_page ?? 10,
      total: apiData.pagination?.total ?? 0,
      totalPages: apiData.pagination?.total_pages ?? 0,
      hasNext: apiData.pagination?.has_next ?? false,
      hasPrev: apiData.pagination?.has_prev ?? false,
    },
    meta: {
      examType: apiData.exam_type,
      className: apiData.class_name,
      sectionId: apiData.section_id,
      academicYear: apiData.academic_year,
      staffName: apiData.staff_name,
    },
  };
};

const buildSubjectProgressChart = (apiData) => {
  const passed = Number(apiData?.passed_percentage) || 0;
  const failed = Number(apiData?.failed_percentage) || 0;
  const unattended = Number(apiData?.unattended_percentage) || 0;
  return {
    labels: ["Unattended", "Failed", "Passed"],
    datasets: [
      { label: "Passed", data: [0, 0, passed] },
      { label: "Failed", data: [0, failed, 0] },
      { label: "Unattended", data: [unattended, 0, 0] },
    ],
  };
};

/* Class-teacher Assessment Progress Tracker.
 * API percentages already sum to ~100, so map them directly. */
const mapAssessmentTracker = (raw) => {
  const list = Array.isArray(raw?.data) ? raw.data : [];
  return list.map((item) => ({
    label: item.subject,
    pass: Number(item.pass_percentage) || 0,
    fail: Number(item.fail_percentage) || 0,
    unattended: Number(item.unattended_percentage) || 0,
    total_students: item.total_students ?? 0,
  }));
};

/* Class-teacher Remedial Progress Tracker.
 * Remedial pass/fail percentages are relative to *attended* only, so they
 * don't sum to 100 with unattended. Derive all three from the raw counts
 * against total_assigned to make a clean 100%-stacked bar. */
const mapRemedialTracker = (raw) => {
  const list = Array.isArray(raw?.data) ? raw.data : [];
  return list.map((item) => {
    const assigned = Number(item.total_assigned) || 0;
    const passCount = Number(item.pass_count) || 0;
    const failCount = Number(item.fail_count) || 0;
    const unattended = Number(item.total_unattended) || 0;
    const pct = (n) => (assigned > 0 ? Math.round((n / assigned) * 100) : 0);
    return {
      label: item.subject,
      pass: pct(passCount),
      fail: pct(failCount),
      unattended: pct(unattended),
      total_assigned: assigned,
    };
  });
};

const mapTopicPerformanceResponse = (raw) => {
  const list = Array.isArray(raw?.data) ? raw.data : [];
  return list.map((item) => ({
    label: item.topic,
    strong: Number(item.strong_percentage) || 0,
    weak: Number(item.weak_percentage) || 0,
    total_students: item.total_students ?? 0,
  }));
};

/* ── Assessment Trend (stacked bar chart) — /dashboard/assessmentTracker ──
 * Each point becomes one assessment. The stacked bar plots the raw
 * fail_count (red) and total_remedial (green) per assessment.
 * assessmentId is carried so the drilldown table can be fetched lazily on
 * click. students starts null and is patched in once the remedial API
 * resolves for that point. */
const mapAssessmentTrackerResponse = (raw) => {
  const list = Array.isArray(raw?.data) ? raw.data : [];
  return list.map((item) => {
    const total = Number(item.total_students) || 0;
    const pass = Number(item.pass_count) || 0;
    const fail = Number(item.fail_count) || 0;
    const totalRemedial = Number(item.total_remedial) || 0;
    const ftrRatio = Number(item.ftr_ratio) || 0;
    // assessment_label can arrive as a Buffer (binary string from MySQL) -> only use it when it is text
    const label =
      (typeof item.assessment_label === "string" && item.assessment_label) ||
      `Assessment ${item.assessment_no ?? ""}`.trim();
    return {
      assessmentId: item.assessment_id,
      label,
      subject: item.subject || "",
      subjectId: item.subject_id ?? "",
      topic: item.topic || "",
      total,
      pass,
      fail,
      totalRemedial, // ← new
      ftrRatio, // ← new
      notAttempted: Number(item.not_attempted) || 0,
      percentage: total > 0 ? Math.round((pass / total) * 100) : 0,
      students: null,
      // For FTR chart tooltip
      createdAt: item.created_at || null,
    };
  });
};

/* ── Assessment Trend drilldown — /dashboard/assessmentTrackerRemedi ── */
const mapRemedialDrilldown = (raw) => {
  const list = Array.isArray(raw?.data) ? raw.data : [];
  return list.map((s) => ({
    id: s.student_id,
    name: s.student_name,
    totalRemedials: Number(s.total_remedials) || 0,
    pass: Number(s.remedial_pass_count) || 0,
    fail: Number(s.remedial_fail_count) || 0,
    pending: Number(s.remedial_pending_count) || 0,
    finalStatus: s.final_status || "",
    lastAttempt: s.last_attempt_at || "",
  }));
};

const mapClassSubjects = (raw) => {
  const list = Array.isArray(raw?.data) ? raw.data : [];
  // De-dupe by subject_id while keeping order.
  const seen = new Set();
  const options = [];
  list.forEach((s) => {
    const value = String(s.subject_id);
    if (seen.has(value)) return;
    seen.add(value);
    options.push({ value, label: s.subject_name || `Subject ${value}` });
  });
  return options;
};

/* ── FTR chart date formatter ── */
const formatFtrDate = (dateStr) => {
  if (!dateStr) return "";
  const s = String(dateStr);
  if (s.includes("T")) {
    const dt = new Date(s);
    if (!Number.isNaN(dt.getTime())) {
      const day = String(dt.getDate()).padStart(2, "0");
      const month = String(dt.getMonth() + 1).padStart(2, "0");
      const year = dt.getFullYear();
      return `${day}-${month}-${year}`;
    }
  }
  const parts = s.split("-");
  if (parts.length === 3 && parts[0].length === 4) {
    return `${parts[2].slice(0, 2)}-${parts[1]}-${parts[0]}`;
  }
  return s;
};

/* ── Adaptive y-axis for FTR chart ── */
const ftrNiceScale = (rawMax, floor = 1) => {
  const target = Math.max(Number(rawMax) || 0, floor);
  if (!Number.isFinite(target) || target <= 0) {
    return { max: Math.max(floor, 2), step: Math.max(floor, 2) / 5 };
  }
  const rough = target / 5;
  const pow = Math.pow(10, Math.floor(Math.log10(rough)));
  const frac = rough / pow;
  let niceFrac;
  if (frac <= 1) niceFrac = 1;
  else if (frac <= 2) niceFrac = 2;
  else if (frac <= 2.5) niceFrac = 2.5;
  else if (frac <= 5) niceFrac = 5;
  else niceFrac = 10;
  const step = niceFrac * pow;
  const max = Math.ceil(target / step) * step;
  return { max, step };
};

/* ─────────────────────────────────────────────────────────────────────────
 *  Component
 * ───────────────────────────────────────────────────────────────────────── */
const AiDashboard = () => {
  const navigate = useNavigate();
  const { translations } = useSelector((state) => state.language);
  const t = useMemo(() => translations?.labels || {}, [translations?.labels]);

  // Generic error message — used for all API failures so we never leak
  // server-side message text to the UI.
  const genericError = useMemo(
    () => t.noDataAvailable || "No data available",
    [t],
  );

  /* ── Mount tracking ── */
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /* ── Auth ── */
  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const school_id = clientData?.school_id || "";
  const academic_id = clientData?.academic_year_id || "";
  const user_id = user?.id || "";
  const staff_Id = clientData?.master_id || "";
  const startDate = clientData?.start_date || "";
  const endDate = clientData?.end_date || "";
  const staff_id = user_id;

  /* ── Abort refs ── */
  const subjectsAbortRef = useRef(null);
  const dailyAbortRef = useRef(null);
  const rateAbortRef = useRef(null);
  const trendAbortRef = useRef(null);
  const assessmentAbortRef = useRef(null);
  const remedialAbortRef = useRef(null);
  const ahsAbortRef = useRef(null);
  const healthScoreAbortRef = useRef(null);
  const subjAbortRef = useRef(null);
  const heatmapAbortRef = useRef(null);
  const syllabusAbortRef = useRef(null);
  const examAbortRef = useRef(null);
  const examListAbortRef = useRef(null);
  const subjAssessmentAbortRef = useRef(null);
  const subjRemedialAbortRef = useRef(null);
  const topicAbortRef = useRef(null);
  const [subjectHealthData, setSubjectHealthData] = useState(
    EMPTY_ACADEMIC_HEALTH,
  );
  const [subjectHealthLoading, setSubjectHealthLoading] = useState(false);
  const [subjectHealthError, setSubjectHealthError] = useState(null);
  const [studentMonth, setStudentMonth] = useState("all"); // "all" = no filter (default)
  const [atLevel, setAtLevel] = useState("subject"); // "subject" | "assessment"
  const [atDrillSubject, setAtDrillSubject] = useState({ id: "", label: "" });
  const [ftrLevel, setFtrLevel] = useState("subject");
  const [ftrDrillSubject, setFtrDrillSubject] = useState({ id: "", label: "" });
  // near other abort refs
  const subjectHealthAbortRef = useRef(null);
  // Assessment Trend refs
  const atSubjectsAbortRef = useRef(null);
  const atAbortRef = useRef(null);
  const atRemedialAbortRef = useRef(null);
  // FTR chart refs
  const ftrSubjectsAbortRef = useRef(null);
  const ftrAbortRef = useRef(null);

  /* ── Class / Section dropdown ── */
  const [classOptions, setClassOptions] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);

  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [subjectsError, setSubjectsError] = useState(null);

  /* ── Class-teacher state ── */
  const [attendanceSummary, setAttendanceSummary] = useState({
    total: { count: 0, male: 0, female: 0 },
    present: { count: 0, male: 0, female: 0 },
    absent: { count: 0, male: 0, female: 0 },
    leave: { count: 0, male: 0, female: 0 },
  });
  const [absentees, setAbsentees] = useState([]);
  const [dailyLoading, setDailyLoading] = useState(false);
  const [dailyError, setDailyError] = useState(null);

  const [attendanceRateData, setAttendanceRateData] = useState(null);
  const [rateLoading, setRateLoading] = useState(false);
  const [rateError, setRateError] = useState(null);

  const [attendanceTrend, setAttendanceTrend] = useState([]);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendError, setTrendError] = useState(null);

  const [assessmentProgress, setAssessmentProgress] = useState([]);
  const [assessmentLoading, setAssessmentLoading] = useState(false);
  const [assessmentError, setAssessmentError] = useState(null);

  const [remedialProgress, setRemedialProgress] = useState([]);
  const [remedialLoading, setRemedialLoading] = useState(false);
  const [remedialError, setRemedialError] = useState(null);

  const [performanceData, setPerformanceData] = useState([]);
  const [examLoading, setExamLoading] = useState(false);
  const [examError, setExamError] = useState(null);

  const [syllabusData, setSyllabusData] = useState([]);
  const [syllabusLoading, setSyllabusLoading] = useState(false);
  const [syllabusError, setSyllabusError] = useState(null);

  /* ── Academic Health Score donut state (CustomDonutChart, class teacher) ── */
  const [healthData, setHealthData] = useState(EMPTY_ACADEMIC_HEALTH);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthError, setHealthError] = useState(null);

  /* ── Subject-teacher state ── */
  const [ahsActiveRisk, setAhsActiveRisk] = useState("noRisk");
  const [subjAssessmentData, setSubjAssessmentData] = useState(null);
  const [subjAssessmentLoading, setSubjAssessmentLoading] = useState(false);
  const [subjAssessmentError, setSubjAssessmentError] = useState(null);

  const [subjRemedialData, setSubjRemedialData] = useState(null);
  const [subjRemedialLoading, setSubjRemedialLoading] = useState(false);
  const [subjRemedialError, setSubjRemedialError] = useState(null);

  const [topicData, setTopicData] = useState([]);
  const [topicLoading, setTopicLoading] = useState(false);
  const [topicError, setTopicError] = useState(null);

  /* ── Assessment Trend state (shared, both roles) ── */
  const [atSubjectOptions, setAtSubjectOptions] = useState([]);
  const [atSubjectsLoading, setAtSubjectsLoading] = useState(false);
  const [atSelectedSubjectId, setAtSelectedSubjectId] = useState("");
  const [atPoints, setAtPoints] = useState([]);
  const [atLoading, setAtLoading] = useState(false);
  const [atError, setAtError] = useState(null);
  const [atDrilldownLoading, setAtDrilldownLoading] = useState(false);
  const [atDrilldownError, setAtDrilldownError] = useState(null);

  /* ── FTR chart state (Remedial Assessment Fail Count Ratio) ── */
  const [ftrSubjectOptions, setFtrSubjectOptions] = useState([]);
  const [ftrSubjectsLoading, setFtrSubjectsLoading] = useState(false);
  const [ftrSelectedSubjectId, setFtrSelectedSubjectId] = useState("");
  const [ftrPoints, setFtrPoints] = useState([]);
  const [ftrLoading, setFtrLoading] = useState(false);
  const [ftrError, setFtrError] = useState(null);

  /* ── Shared state ── */
  const [ahsData, setAhsData] = useState(null);
  const [ahsStatus, setAhsStatus] = useState("idle");
  const [ahsRiskFilter, setAhsRiskFilter] = useState("");

  const [subjData, setSubjData] = useState(null);
  const [subjStatus, setSubjStatus] = useState("idle");
  const [selectedSubjectId, setSelectedSubjectId] = useState(null);
  const [selectedRiskKey, setSelectedRiskKey] = useState("");

  const [heatmapData, setHeatmapData] = useState(null);
  const [heatmapStatus, setHeatmapStatus] = useState("idle");
  const [heatmapMessage, setHeatmapMessage] = useState("");
  const [heatmapPage, setHeatmapPage] = useState(1);
  const [heatmapLimit, setHeatmapLimit] = useState(10);
  const [heatmapSearch, setHeatmapSearch] = useState("");
  const [debouncedHeatmapSearch, setDebouncedHeatmapSearch] = useState("");

  const [periodOptions, setPeriodOptions] = useState([]);
  const [heatmapPeriod, setHeatmapPeriod] = useState("");

  const isClassTeacher = Number(selectedClass?.class_staff) === 1;

  /* Subject id used to drive the Assessment Trend chart:
   *  - class teacher → chosen from the in-chart subject dropdown
   *  - subject teacher → taken from the top "Filter by class" dropdown */
  const effectiveAtSubjectId = isClassTeacher
    ? atSelectedSubjectId
    : selectedClass?.subject_id
      ? String(selectedClass.subject_id)
      : "";

  /* Subject id used to drive the FTR chart:
   *  - class teacher → chosen from the FTR in-chart subject dropdown
   *  - subject teacher → taken from the top "Filter by class" dropdown */
  const effectiveFtrSubjectId = isClassTeacher
    ? ftrSelectedSubjectId
    : selectedClass?.subject_id
      ? String(selectedClass.subject_id)
      : "";

  const safeSet = useCallback((setter, value, controller) => {
    if (!isMountedRef.current) return;
    if (controller && controller.signal.aborted) return;
    setter(value);
  }, []);

  /* ==================================================================
   *  Class / Section list
   * ================================================================== */
  const fetchSubjects = useCallback(async () => {
    if ((!staff_Id && !staff_id) || !school_id) return;
    subjectsAbortRef.current?.abort();
    const controller = new AbortController();
    subjectsAbortRef.current = controller;
    safeSet(setSubjectsLoading, true, controller);
    safeSet(setSubjectsError, null, controller);
    try {
      const response = await AxiosInstance.post(
        `/dashboard/staff/getClassSectionList`,
        { staff_id: staff_Id || staff_id, school_id },
        { signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      const rawList = response?.data?.data;
      if (Array.isArray(rawList) && rawList.length > 0) {
        const options = mapClassSectionOptions(rawList);
        safeSet(setClassOptions, options, controller);
        safeSet(setSelectedClass, options[0], controller);
      } else {
        safeSet(setClassOptions, [], controller);
        safeSet(setSelectedClass, null, controller);
        safeSet(setSubjectsError, genericError, controller);
      }
    } catch (err) {
      if (!isAbortError(err)) {
        safeSet(setSubjectsError, genericError, controller);
      }
    } finally {
      if (!controller.signal.aborted) {
        safeSet(setSubjectsLoading, false, controller);
      }
    }
  }, [staff_Id, staff_id, school_id, genericError, safeSet]);

  useEffect(() => {
    fetchSubjects();
    return () => subjectsAbortRef.current?.abort();
  }, [fetchSubjects]);

  /* ==================================================================
   *  Class-teacher fetches
   * ================================================================== */
  const fetchDailyAttendance = useCallback(async () => {
    if (!selectedClass || !school_id) return;
    dailyAbortRef.current?.abort();
    const controller = new AbortController();
    dailyAbortRef.current = controller;
    safeSet(setDailyLoading, true, controller);
    safeSet(setDailyError, null, controller);
    try {
      const response = await AxiosInstance.post(
        `/dashboard/class_teacher/daily-attendance`,
        {
          class_id: selectedClass.class_id,
          section_id: selectedClass.section_id,
          school_id,
          date: todayISO(),
          academic_year: academic_id,
        },
        { signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      const result = response?.data?.data;
      if (result) {
        const s = result.summary;
        safeSet(
          setAttendanceSummary,
          {
            total: s.total_students ?? { count: 0, male: 0, female: 0 },
            present: s.present ?? { count: 0, male: 0, female: 0 },
            absent: s.absent ?? { count: 0, male: 0, female: 0 },
            leave: s.leave ?? { count: 0, male: 0, female: 0 },
          },
          controller,
        );
        safeSet(
          setAbsentees,
          (result.absentees ?? []).map((a) => ({
            id: a.student_id,
            initials: getInitials(a.name),
            name: a.name,
            phone: a.phone,
            gender: a.gender,
          })),
          controller,
        );
      } else {
        safeSet(setDailyError, genericError, controller);
      }
    } catch (err) {
      if (!isAbortError(err)) safeSet(setDailyError, genericError, controller);
    } finally {
      if (!controller.signal.aborted) {
        safeSet(setDailyLoading, false, controller);
      }
    }
  }, [selectedClass, school_id, academic_id, genericError, safeSet]);

  const fetchAttendanceRate = useCallback(async () => {
    if (!selectedClass || !staff_id || !school_id) return;
    rateAbortRef.current?.abort();
    const controller = new AbortController();
    rateAbortRef.current = controller;
    safeSet(setRateLoading, true, controller);
    safeSet(setRateError, null, controller);
    try {
      const response = await AxiosInstance.post(
        `/dashboard/staff/getAttendanceRate`,
        {
          school_id,
          class_id: selectedClass.class_id,
          section_id: selectedClass.section_id,
          staff_id,
          academic_id,
        },
        { signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      const result = response?.data?.data || response?.data;
      if (result) {
        safeSet(
          setAttendanceRateData,
          {
            total: result.total_strength ?? 0,
            segments: [
              {
                key: "present",
                label: t.present || "Present",
                value: result.present_count ?? 0,
                paletteIndex: 1,
              },
              {
                key: "absent",
                label: t.absent || "Absent",
                value: result.absent_count ?? 0,
                paletteIndex: 5,
              },
              {
                key: "leave",
                label: t.leave || "Leave",
                value: result.leave_count ?? 0,
                paletteIndex: 3,
              },
            ],
          },
          controller,
        );
      } else {
        safeSet(setRateError, genericError, controller);
      }
    } catch (err) {
      if (!isAbortError(err)) safeSet(setRateError, genericError, controller);
    } finally {
      if (!controller.signal.aborted) {
        safeSet(setRateLoading, false, controller);
      }
    }
  }, [
    selectedClass,
    school_id,
    staff_id,
    academic_id,
    t,
    genericError,
    safeSet,
  ]);
  /* "4-2026" → { month: 4, year: 2026 }; "" / "all" / unparseable → { month: "", year: "" }
   * Accepts a raw "M-YYYY" string OR an option object { value }, so it can't
   * accidentally produce NaN (which axios would serialise as null). */
  const parseMonthValue = (val) => {
    const raw = val && typeof val === "object" ? val.value : val;
    if (!raw) return { month: "", year: "" };
    const [m, y] = String(raw).split("-");
    const month = Number(m);
    const year = Number(y);
    if (!Number.isFinite(month) || !Number.isFinite(year)) {
      return { month: "", year: "" };
    }
    return { month, year };
  };
  const fetchAttendanceTrend = useCallback(async () => {
    if (!selectedClass || !staff_id || !school_id) return;
    trendAbortRef.current?.abort();
    const controller = new AbortController();
    trendAbortRef.current = controller;
    safeSet(setTrendLoading, true, controller);
    safeSet(setTrendError, null, controller);
    try {
      const { month, year } = parseMonthValue(studentMonth);

      const response = await AxiosInstance.post(
        `/dashboard/staff/getAttendanceTrend`,
        {
          school_id,
          class_id: selectedClass.class_id,
          section_id: selectedClass.section_id,
          staff_id,
          academic_id,
          month,
          year,
        },
        { signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      const result = response?.data?.data || response?.data;
      if (result?.trend) {
        safeSet(
          setAttendanceTrend,
          result.trend.map((e) => ({
            date: e.date ?? "",
            present_count: e.present_count ?? 0,
            absent_count: e.absent_count ?? 0,
            leave_count: e.leave_count ?? 0,
            total_strength: e.total_strength ?? 0,
            attendance_percent: e.attendance_percent ?? 0,
          })),
          controller,
        );
      } else {
        safeSet(setTrendError, genericError, controller);
      }
    } catch (err) {
      if (!isAbortError(err)) safeSet(setTrendError, genericError, controller);
    } finally {
      if (!controller.signal.aborted) {
        safeSet(setTrendLoading, false, controller);
      }
    }
  }, [
    selectedClass,
    school_id,
    staff_id,
    academic_id,
    genericError,
    ,
    studentMonth,
    safeSet,
  ]);

  const fetchAssessmentProgress = useCallback(async () => {
    if (!selectedClass || !school_id) return;
    assessmentAbortRef.current?.abort();
    const controller = new AbortController();
    assessmentAbortRef.current = controller;
    safeSet(setAssessmentLoading, true, controller);
    safeSet(setAssessmentError, null, controller);
    try {
      const response = await AxiosInstance.post(
        `/dashboard/class_teacher/assessment-progress-tracker`,
        {
          school_id,
          class_id: selectedClass.class_id,
          section_id: selectedClass.section_id,
          academic_year: academic_id,
        },
        { signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      const result = response?.data?.data;
      if (Array.isArray(result)) {
        safeSet(
          setAssessmentProgress,
          mapAssessmentTracker(response.data),
          controller,
        );
      } else {
        safeSet(setAssessmentError, genericError, controller);
      }
    } catch (err) {
      if (!isAbortError(err))
        safeSet(setAssessmentError, genericError, controller);
    } finally {
      if (!controller.signal.aborted) {
        safeSet(setAssessmentLoading, false, controller);
      }
    }
  }, [selectedClass, school_id, academic_id, genericError, safeSet]);

  const fetchRemedialProgress = useCallback(async () => {
    if (!selectedClass || !school_id) return;
    remedialAbortRef.current?.abort();
    const controller = new AbortController();
    remedialAbortRef.current = controller;
    safeSet(setRemedialLoading, true, controller);
    safeSet(setRemedialError, null, controller);
    try {
      const response = await AxiosInstance.post(
        `/dashboard/class_teacher/class-remedial-tracker`,
        {
          school_id,
          class_id: selectedClass.class_id,
          section_id: selectedClass.section_id,
          academic_year: academic_id,
        },
        { signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      const result = response?.data?.data;
      if (Array.isArray(result)) {
        safeSet(
          setRemedialProgress,
          mapRemedialTracker(response.data),
          controller,
        );
      } else {
        safeSet(setRemedialError, genericError, controller);
      }
    } catch (err) {
      if (!isAbortError(err))
        safeSet(setRemedialError, genericError, controller);
    } finally {
      if (!controller.signal.aborted) {
        safeSet(setRemedialLoading, false, controller);
      }
    }
  }, [selectedClass, school_id, academic_id, genericError, safeSet]);

  const fetchExamPerformance = useCallback(async () => {
    if (!selectedClass || !school_id) return;
    examAbortRef.current?.abort();
    const controller = new AbortController();
    examAbortRef.current = controller;
    safeSet(setExamLoading, true, controller);
    safeSet(setExamError, null, controller);
    try {
      const response = await AxiosInstance.post(
        `/dashboard/class_teacher/examwisePerformance`,
        {
          school_id: Number(school_id),
          class_id: selectedClass.class_id,
          section_id: selectedClass.section_id,
          academic_year: academic_id,
        },
        { signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      const apiMessage = response?.data?.message;
      const apiData = response?.data?.data;
      if (response?.data?.status && Array.isArray(response.data.data)) {
        safeSet(
          setPerformanceData,
          apiData.map((item, index) => ({
            exam: item.exam_name,
            passPercentage: Number(item.pass_percentage),
            failPercentage: Number(item.fail_percentage),
            colorIndex: index,
            subjects: item.subjects || [],
          })),
          controller,
        );
      } else {
        safeSet(setPerformanceData, [], controller);
        safeSet(setExamError, apiMessage || genericError, controller);
      }
    } catch (err) {
      if (!isAbortError(err)) {
        const apiMessage = err?.response?.data?.message;
        safeSet(setExamError, apiMessage || genericError, controller);
      }
    } finally {
      if (!controller.signal.aborted) {
        safeSet(setExamLoading, false, controller);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClass, school_id, genericError, safeSet]);

  // const fetchSyllabusCompletion = useCallback(async () => {
  //   if (!selectedClass || !school_id) return;
  //   syllabusAbortRef.current?.abort();
  //   const controller = new AbortController();
  //   syllabusAbortRef.current = controller;
  //   safeSet(setSyllabusLoading, true, controller);
  //   safeSet(setSyllabusError, null, controller);
  //   try {
  //     const response = await AxiosInstance.post(
  //       `/dashboard/hm-dashboard/syllabus-completion`,
  //       {
  //         school_id,
  //         class_id: selectedClass.class_id,
  //         section_id: selectedClass.section_id,
  //         academic_year: academic_id,
  //       },
  //       { signal: controller.signal },
  //     );
  //     if (controller.signal.aborted) return;
  //     if (response?.data?.success && Array.isArray(response.data.data)) {
  //       safeSet(
  //         setSyllabusData,
  //         mapSyllabusCompletionResponse(response.data),
  //         controller,
  //       );
  //     } else {
  //       safeSet(setSyllabusData, [], controller);
  //       safeSet(setSyllabusError, genericError, controller);
  //     }
  //   } catch (err) {
  //     if (!isAbortError(err))
  //       safeSet(setSyllabusError, genericError, controller);
  //   } finally {
  //     if (!controller.signal.aborted) {
  //       safeSet(setSyllabusLoading, false, controller);
  //     }
  //   }
  // }, [selectedClass, school_id, genericError, safeSet]);
  const fetchSyllabusMonth = useCallback(async () => {
    if (!school_id || !selectedClass?.class_id || !academic_id) return;

    // Subject teacher must have a subject_id to fetch; class teacher sends empty
    if (!isClassTeacher && !selectedClass?.subject_id) return;

    syllabusAbortRef.current?.abort();
    const controller = new AbortController();
    syllabusAbortRef.current = controller;
    safeSet(setSyllabusLoading, true, controller);
    safeSet(setSyllabusError, null, controller);

    try {
      const response = await AxiosInstance.post(
        `/dashboard/syllabusMonth`,
        {
          school_id: Number(school_id),
          class_id: selectedClass.class_id,
          section_id: selectedClass.section_id || "",
          academic_id,
          page: "",
          limit: "",
          staff_id: staff_Id,
          subject_id: isClassTeacher ? "" : Number(selectedClass.subject_id),
        },
        { signal: controller.signal },
      );
      if (controller.signal.aborted) return;

      const apiMessage = response?.data?.message;
      const classes = response?.data?.data?.classes;
      if (response?.data?.status && Array.isArray(classes)) {
        safeSet(
          setSyllabusData,
          mapSyllabusCompletionResponse(response.data),
          controller,
        );
        safeSet(setSyllabusError, null, controller);
      } else {
        safeSet(setSyllabusData, [], controller);
        safeSet(setSyllabusError, apiMessage || genericError, controller);
      }
    } catch (err) {
      if (!isAbortError(err)) {
        const apiMessage = err?.response?.data?.message;
        safeSet(setSyllabusError, apiMessage || genericError, controller);
      }
    } finally {
      if (!controller.signal.aborted) {
        safeSet(setSyllabusLoading, false, controller);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    school_id,
    selectedClass?.class_id,
    selectedClass?.section_id,
    selectedClass?.subject_id, // refetch when subject changes
    selectedClass?.class_staff, // refetch when role flag changes
    academic_id,
    isClassTeacher,
    genericError,
    safeSet,
  ]);
  /* Academic Health Score donut (class teacher only).
   * POST /dashboard/class_teacher/academic_healthscore
   * payload: { staff_id, school_id, class_id, section_id, academic_id } */
  const loadAcademicHealthScore = useCallback(async () => {
    if (
      !selectedClass?.class_id ||
      !selectedClass?.section_id ||
      !school_id ||
      !staff_id ||
      !academic_id
    )
      return;
    healthScoreAbortRef.current?.abort();
    const controller = new AbortController();
    healthScoreAbortRef.current = controller;
    safeSet(setHealthLoading, true, controller);
    safeSet(setHealthError, null, controller);
    try {
      const response = await AxiosInstance.post(
        `/dashboard/class_teacher/academic_healthscore`,
        {
          staff_id,
          school_id,
          class_id: selectedClass.class_id,
          section_id: selectedClass.section_id,
          academic_id,
          staff_id: staff_id,
        },
        { signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      if (response?.data?.status && response.data.data) {
        safeSet(
          setHealthData,
          mapAcademicHealthScoreResponse(response.data),
          controller,
        );
      } else {
        // Keep breakdown mode (zeros) and surface a generic error.
        safeSet(setHealthData, EMPTY_ACADEMIC_HEALTH, controller);
        safeSet(setHealthError, genericError, controller);
      }
    } catch (err) {
      if (!isAbortError(err)) {
        safeSet(setHealthData, EMPTY_ACADEMIC_HEALTH, controller);
        safeSet(setHealthError, genericError, controller);
      }
    } finally {
      if (!controller.signal.aborted) {
        safeSet(setHealthLoading, false, controller);
      }
    }
  }, [selectedClass, school_id, staff_id, academic_id, genericError, safeSet]);

  const fetchSubjectAssessmentProgress = useCallback(async () => {
    if (
      !selectedClass?.class_id ||
      !selectedClass?.section_id ||
      !selectedClass?.subject_id ||
      !school_id
    )
      return;
    subjAssessmentAbortRef.current?.abort();
    const controller = new AbortController();
    subjAssessmentAbortRef.current = controller;
    safeSet(setSubjAssessmentLoading, true, controller);
    safeSet(setSubjAssessmentError, null, controller);
    try {
      const response = await AxiosInstance.post(
        `/dashboard/subject_staff/assessment-progress-tracker`,
        {
          school_id,
          class_id: selectedClass.class_id,
          section_id: selectedClass.section_id,
          subject_id: Number(selectedClass.subject_id),
          academic_year: academic_id,
          staff_id: user_id,
        },
        { signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      if (response?.data?.status && response.data.data) {
        safeSet(setSubjAssessmentData, response.data.data, controller);
      } else {
        safeSet(setSubjAssessmentData, null, controller);
        safeSet(setSubjAssessmentError, genericError, controller);
      }
    } catch (err) {
      if (!isAbortError(err))
        safeSet(setSubjAssessmentError, genericError, controller);
    } finally {
      if (!controller.signal.aborted) {
        safeSet(setSubjAssessmentLoading, false, controller);
      }
    }
  }, [selectedClass, school_id, academic_id, genericError, safeSet]);

  const fetchSubjectRemedialProgress = useCallback(async () => {
    if (
      !selectedClass?.class_id ||
      !selectedClass?.section_id ||
      !selectedClass?.subject_id ||
      !school_id
    )
      return;
    subjRemedialAbortRef.current?.abort();
    const controller = new AbortController();
    subjRemedialAbortRef.current = controller;
    safeSet(setSubjRemedialLoading, true, controller);
    safeSet(setSubjRemedialError, null, controller);
    try {
      const response = await AxiosInstance.post(
        `/dashboard/subject_staff/remedial-progress-tracker`,
        {
          school_id,
          class_id: selectedClass.class_id,
          section_id: selectedClass.section_id,
          subject_id: Number(selectedClass.subject_id),
          academic_year: academic_id,
          staff_id: user_id,
        },
        { signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      if (response?.data?.status && response.data.data) {
        safeSet(setSubjRemedialData, response.data.data, controller);
      } else {
        safeSet(setSubjRemedialData, null, controller);
        safeSet(setSubjRemedialError, genericError, controller);
      }
    } catch (err) {
      if (!isAbortError(err))
        safeSet(setSubjRemedialError, genericError, controller);
    } finally {
      if (!controller.signal.aborted) {
        safeSet(setSubjRemedialLoading, false, controller);
      }
    }
  }, [selectedClass, school_id, academic_id, genericError, safeSet]);

  const fetchTopicPerformance = useCallback(async () => {
    if (
      !selectedClass?.class_id ||
      !selectedClass?.section_id ||
      !selectedClass?.subject_id ||
      !school_id ||
      !user_id ||
      !academic_id
    )
      return;
    topicAbortRef.current?.abort();
    const controller = new AbortController();
    topicAbortRef.current = controller;
    safeSet(setTopicLoading, true, controller);
    safeSet(setTopicError, null, controller);
    try {
      const response = await AxiosInstance.post(
        `/dashboard/subject_teacher/topic_performance`,
        {
          school_id,
          class_id: selectedClass.class_id,
          section: selectedClass.section_id,
          subject: Number(selectedClass.subject_id),
          staff_id: user_id,
          academic_year_id: academic_id,
        },
        { signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      if (response?.data?.status && Array.isArray(response.data.data)) {
        safeSet(
          setTopicData,
          mapTopicPerformanceResponse(response.data),
          controller,
        );
      } else {
        safeSet(setTopicData, [], controller);
        safeSet(setTopicError, genericError, controller);
      }
    } catch (err) {
      if (!isAbortError(err)) safeSet(setTopicError, genericError, controller);
    } finally {
      if (!controller.signal.aborted) {
        safeSet(setTopicLoading, false, controller);
      }
    }
  }, [selectedClass, school_id, user_id, academic_id, genericError, safeSet]);

  /* ==================================================================
   *  Assessment Trend fetches (shared)
   * ================================================================== */

  /* Class teacher only — fetch the subject list for the in-chart dropdown
   * and default to the first subject_id. */
  const fetchAssessmentTrendSubjects = useCallback(async () => {
    if (!isClassTeacher || !selectedClass?.class_id || !school_id) return;
    atSubjectsAbortRef.current?.abort();
    const controller = new AbortController();
    atSubjectsAbortRef.current = controller;
    safeSet(setAtSubjectsLoading, true, controller);
    try {
      const response = await AxiosInstance.post(
        "/school/class/subjects",
        {
          class_id: String(selectedClass.class_id),
          section: "",
          shift_id: "",
          school_id: Number(school_id),
        },
        { signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      const options =
        response?.data?.success || Array.isArray(response?.data?.data)
          ? mapClassSubjects(response.data)
          : [];
      safeSet(setAtSubjectOptions, options, controller);
      safeSet(setAtSelectedSubjectId, options[0]?.value || "", controller);
    } catch (err) {
      if (!isAbortError(err)) {
        safeSet(setAtSubjectOptions, [], controller);
        safeSet(setAtSelectedSubjectId, "", controller);
      }
    } finally {
      if (!controller.signal.aborted) {
        safeSet(setAtSubjectsLoading, false, controller);
      }
    }
  }, [isClassTeacher, selectedClass?.class_id, school_id, safeSet]);

  /* Stacked bar data from /dashboard/assessmentTracker.
   * payload: { school_id, class_id, section_id, subject_id, academic_year } */
  const fetchAssessmentTracker = useCallback(async () => {
    if (
      !selectedClass?.class_id ||
      !selectedClass?.section_id ||
      !school_id ||
      !academic_id
    )
      return;
    atAbortRef.current?.abort();
    const controller = new AbortController();
    atAbortRef.current = controller;
    safeSet(setAtLoading, true, controller);
    safeSet(setAtError, null, controller);
    try {
      const response = await AxiosInstance.post(
        `/dashboard/assessmentTracker`,
        {
          school_id: Number(school_id),
          class_id: selectedClass.class_id,
          section_id: selectedClass.section_id,
          subject_id:
            atLevel === "assessment"
              ? String(atDrillSubject.id)
              : Number(selectedClass.subject_id) || "",

          academic_year: academic_id,
        },
        { signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      if (response?.data?.status && Array.isArray(response.data.data)) {
        safeSet(
          setAtPoints,
          mapAssessmentTrackerResponse(response.data),
          controller,
        );
      } else {
        safeSet(setAtPoints, [], controller);
        safeSet(setAtError, genericError, controller);
      }
    } catch (err) {
      if (!isAbortError(err)) {
        safeSet(setAtPoints, [], controller);
        safeSet(setAtError, genericError, controller);
      }
    } finally {
      if (!controller.signal.aborted) {
        safeSet(setAtLoading, false, controller);
      }
    }
  }, [
    selectedClass,
    school_id,
    academic_id,
    atLevel,
    effectiveAtSubjectId,
    genericError,
    safeSet,
  ]);
  /* Lazy drilldown — fired by LineAndBarChart when a bar is clicked.
   * Fetches /dashboard/assessmentTrackerRemedi for that assessment_id and
   * patches the rows into atPoints[index].students. */
  const fetchAssessmentRemedial = useCallback(
    async (index) => {
      const point = atPoints[index];
      if (!point) return;
      // Already loaded for this point — nothing to do.
      if (Array.isArray(point.students)) {
        setAtDrilldownLoading(false);
        setAtDrilldownError(null);
        return;
      }
      atRemedialAbortRef.current?.abort();
      const controller = new AbortController();
      atRemedialAbortRef.current = controller;
      setAtDrilldownLoading(true);
      setAtDrilldownError(null);
      try {
        const response = await AxiosInstance.post(
          `/dashboard/assessmentTrackerRemedi`,
          { assessment_id: point.assessmentId },
          { signal: controller.signal },
        );
        if (controller.signal.aborted) return;
        const rows = Array.isArray(response?.data?.data)
          ? response.data.data
          : [];
        if (response?.data?.status && rows.length > 0) {
          const students = mapRemedialDrilldown(response.data);
          if (!isMountedRef.current) return;
          setAtPoints((prev) =>
            prev.map((p, i) => (i === index ? { ...p, students } : p)),
          );
        } else {
          // e.g. { status:false, message:"No Assessment found!", data:{} }
          setAtDrilldownError(response?.data?.message || genericError);
        }
      } catch (err) {
        if (!isAbortError(err)) setAtDrilldownError(genericError);
      } finally {
        if (!controller.signal.aborted && isMountedRef.current) {
          setAtDrilldownLoading(false);
        }
      }
    },
    [atPoints, genericError],
  );
  const handleAtBarClick = useCallback(
    (index) => {
      if (atLevel === "subject") {
        const row = atPoints[index];
        if (!row?.subjectId) return;
        setAtDrillSubject({ id: row.subjectId, label: row.subject });
        setAtLevel("assessment"); // effect refetches → assessment bars (with assessment_id)
      } else {
        fetchAssessmentRemedial(index); // assessment level → remedial student table (existing)
      }
    },
    [atLevel, atPoints, fetchAssessmentRemedial],
  );
  /* Tooltip — only Fail Count (dataset 0) and Remedial Count (dataset 1). */
  const assessmentTrendTooltipFormatter = useCallback(
    (idx, datasetIndex) => {
      const row = atPoints[idx];
      if (!row) return null;

      if (datasetIndex === 0) return `Fail Count: ${row.fail}`;
      if (datasetIndex === 1) {
        return [
          `Remedial Count: ${row.totalRemedial}`,
          `${t.ftrRatio || "FTR Ratio"}: ${Number(row.ftrRatio ?? 0).toFixed(2)}`,
        ];
      }
      return null;
    },
    [atPoints],
  );

  /* Assessment Trend — tooltip title: "Assessment N" + "Date: DD-MM-YYYY" */
  const assessmentTrendTooltipTitleFormatter = useCallback(
    (dataIndex) => {
      const row = atPoints[dataIndex];
      if (!row) return "";
      if (atLevel === "subject") return row.subject || row.label;
      return row.createdAt
        ? [row.label, ` ${formatFtrDate(row.createdAt)}`]
        : row.label;
    },
    [atPoints, atLevel],
  );

  /* ==================================================================
   *  FTR chart fetches (Remedial Assessment Fail Count Ratio)
   * ================================================================== */

  /* Class teacher only — fetch the subject list for the FTR in-chart dropdown
   * and default to the first subject_id. Reuses the same /school/class/subjects
   * endpoint as the Assessment Trend subject dropdown. */
  const fetchFtrSubjects = useCallback(async () => {
    if (!isClassTeacher || !selectedClass?.class_id || !school_id) return;
    ftrSubjectsAbortRef.current?.abort();
    const controller = new AbortController();
    ftrSubjectsAbortRef.current = controller;
    safeSet(setFtrSubjectsLoading, true, controller);
    try {
      const response = await AxiosInstance.post(
        "/school/class/subjects",
        {
          class_id: String(selectedClass.class_id),
          section: "",
          shift_id: "",
          school_id: Number(school_id),
        },
        { signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      const options =
        response?.data?.success || Array.isArray(response?.data?.data)
          ? mapClassSubjects(response.data)
          : [];
      safeSet(setFtrSubjectOptions, options, controller);
      // Default to first subject
      safeSet(setFtrSelectedSubjectId, options[0]?.value || "", controller);
    } catch (err) {
      if (!isAbortError(err)) {
        safeSet(setFtrSubjectOptions, [], controller);
        safeSet(setFtrSelectedSubjectId, "", controller);
      }
    } finally {
      if (!controller.signal.aborted) {
        safeSet(setFtrSubjectsLoading, false, controller);
      }
    }
  }, [isClassTeacher, selectedClass?.class_id, school_id, safeSet]);

  /* Fetch FTR data from /dashboard/assessmentTracker — same endpoint as
   * Assessment Trend but we only use the ftr_ratio field per assessment. */
  const fetchFtrData = useCallback(async () => {
    if (
      !selectedClass?.class_id ||
      !selectedClass?.section_id ||
      !school_id ||
      !academic_id
    )
      return;
    ftrAbortRef.current?.abort();
    const controller = new AbortController();
    ftrAbortRef.current = controller;
    safeSet(setFtrLoading, true, controller);
    safeSet(setFtrError, null, controller);
    try {
      const response = await AxiosInstance.post(
        `/dashboard/assessmentTracker`,
        {
          school_id: Number(school_id),
          class_id: selectedClass.class_id,
          section_id: selectedClass.section_id,
          subject_id:
            ftrLevel === "assessment"
              ? String(ftrDrillSubject.id)
              : Number(selectedClass.subject_id) || "",
          academic_year: academic_id,
        },
        { signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      if (response?.data?.status && Array.isArray(response.data.data)) {
        safeSet(
          setFtrPoints,
          mapAssessmentTrackerResponse(response.data),
          controller,
        );
      } else {
        safeSet(setFtrPoints, [], controller);
        safeSet(setFtrError, genericError, controller);
      }
    } catch (err) {
      if (!isAbortError(err)) {
        safeSet(setFtrPoints, [], controller);
        safeSet(setFtrError, genericError, controller);
      }
    } finally {
      if (!controller.signal.aborted) {
        safeSet(setFtrLoading, false, controller);
      }
    }
  }, [
    selectedClass,
    school_id,
    academic_id,
    ftrLevel, // ←
    ftrDrillSubject.id,
    genericError,
    safeSet,
  ]);

  /* ==================================================================
   *  Shared fetches
   * ================================================================== */
  const handleAhsSegmentClick = useCallback((riskKey) => {
    setAhsActiveRisk(riskKey); // ← drives the pop-out
    setAhsRiskFilter(AHS_RISK_CODE[riskKey] ?? null);
  }, []);
  const loadAcademicHealth = useCallback(async () => {
    if (
      !school_id ||
      !selectedClass?.class_id ||
      !selectedClass?.section_id ||
      !user_id ||
      !academic_id
    )
      return;
    // Subject teachers also need a subject_id; bail out if it's missing
    if (!isClassTeacher && !selectedClass?.subject_id) return;

    // Pick endpoint + payload by role
    const endpoint = isClassTeacher
      ? "/dashboard/staff/acadmicHealthScore"
      : "/dashboard/staff/subjectHealthScore";

    const payload = {
      school_id,
      class_id: selectedClass.class_id,
      section_id: selectedClass.section_id,
      staff_id: user_id,
      academic_id,
      ...(isClassTeacher ? {} : { subject_id: selectedClass.subject_id }),
      // FIX 9: 0 = No risk was lost by `ahsRiskFilter || 2` (0 is falsy) -> No-risk click returned the High-risk list
      ...(ahsRiskFilter !== null
        ? { risk_filter: ahsRiskFilter === "" ? 2 : ahsRiskFilter }
        : {}),
    };

    ahsAbortRef.current?.abort();
    const controller = new AbortController();
    ahsAbortRef.current = controller;
    safeSet(setAhsStatus, "loading", controller);
    try {
      const { data } = await AxiosInstance.post(endpoint, payload, {
        signal: controller.signal,
      });
      setAhsData(mapAhsResponse(data));
      setAhsStatus("success");
    } catch (err) {
      if (!isAbortError(err)) safeSet(setAhsStatus, "error", controller);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    school_id,
    selectedClass?.class_id,
    selectedClass?.section_id,
    selectedClass?.class_staff, // ← refetch if role flag changes
    selectedClass?.subject_id, // ← refetch when subject changes
    user_id,
    academic_id,
    ahsRiskFilter,
    safeSet,
  ]);
  const loadSubjectHealth = useCallback(async () => {
    if (
      !selectedClass?.class_id ||
      !selectedClass?.section_id ||
      !selectedClass?.subject_id || // subject_id is mandatory for this endpoint
      !school_id ||
      !staff_id ||
      !academic_id
    )
      return;

    subjectHealthAbortRef.current?.abort();
    const controller = new AbortController();
    subjectHealthAbortRef.current = controller;
    safeSet(setSubjectHealthLoading, true, controller);
    safeSet(setSubjectHealthError, null, controller);

    try {
      const response = await AxiosInstance.post(
        `/dashboard/subjectAcademicHealthScore`,
        {
          staff_id: staff_id,
          school_id: Number(school_id),
          class_id: selectedClass.class_id,
          section_id: selectedClass.section_id,
          subject_id: Number(selectedClass.subject_id),
          academic_id,

          // staff_id: 5272,
          // school_id: 8,
          // class_id: 66,
          // section_id: "A",
          // subject_id: 27,
          // academic_id: 9
        },
        { signal: controller.signal },
      );
      if (controller.signal.aborted) return;

      if (response?.data?.status && response.data.data) {
        safeSet(
          setSubjectHealthData,
          mapSubjectAcademicHealthScoreResponse(response.data), // ← reuse existing mapper
          controller,
        );
      } else {
        const apiMessage = response?.data?.message;
        if (response?.data?.status && response.data.data) {
          safeSet(
            setSubjectHealthData,
            mapSubjectAcademicHealthScoreResponse(response.data),
            controller,
          );
        } else {
          safeSet(setSubjectHealthData, EMPTY_ACADEMIC_HEALTH, controller);
          safeSet(
            setSubjectHealthError,
            apiMessage || genericError,
            controller,
          ); // ← API message wins
        }
      }
    } catch (err) {
      if (!isAbortError(err)) {
        const apiMessage = err?.response?.data?.message;
        safeSet(setSubjectHealthData, EMPTY_ACADEMIC_HEALTH, controller);
        safeSet(setSubjectHealthError, apiMessage || genericError, controller);
      }
    } finally {
      if (!controller.signal.aborted) {
        safeSet(setSubjectHealthLoading, false, controller);
      }
    }
  }, [
    selectedClass?.class_id,
    selectedClass?.section_id,
    selectedClass?.subject_id,
    school_id,
    staff_id,
    staff_Id,
    academic_id,
    genericError,
    safeSet,
  ]);

  const handleSubjectSegmentClick = useCallback((subjectId, riskKey) => {
    setSelectedSubjectId(subjectId);
    setSelectedRiskKey(riskKey);
  }, []);

  const loadSubjectEngagement = useCallback(async () => {
    if (
      !school_id ||
      !selectedClass?.class_id ||
      !selectedClass?.section_id ||
      !user_id ||
      !academic_id
    )
      return;
    // Subject teachers always operate on their assigned subject_id
    if (!isClassTeacher && !selectedClass?.subject_id) return;

    // Risk flags from the currently selected donut segment (1 hot, others 0)
    const riskFlags = {
      no_risk: selectedRiskKey === "noRisk" ? 1 : 0,
      medium_risk: selectedRiskKey === "mediumRisk" ? 1 : 0,
      high_risk: selectedRiskKey === "highRisk" ? 1 : 0,
    };

    // Pick endpoint + payload by role
    const endpoint = isClassTeacher
      ? "/dashboard/class_teacher/class-subject-engagement"
      : "/dashboard/staff/getSubjectEngagementScore";

    const payload = isClassTeacher
      ? {
          school_id,
          class_id: selectedClass.class_id,
          section_id: selectedClass.section_id,
          academic_year: academic_id, // class teacher uses academic_year
          subject_id: Number(selectedClass.subject_id), // subject NAME ("ENGLISH", "TAMIL", ...)
          ...riskFlags,
        }
      : {
          school_id,
          class_id: selectedClass.class_id,
          section_id: selectedClass.section_id,
          academic_id: academic_id, // subject teacher uses academic_id
          subject_id: Number(selectedClass.subject_id), // subject ID (number)
          ...riskFlags,
          staff_id: staff_Id,
        };

    subjAbortRef.current?.abort();
    const controller = new AbortController();
    subjAbortRef.current = controller;
    safeSet(setSubjStatus, "loading", controller);
    try {
      const { data } = await AxiosInstance.post(endpoint, payload, {
        signal: controller.signal,
      });

      const mapped = mapSubjectEngagementResponse(data);
      setSubjData(mapped);
      setSubjStatus("success");
      /* Choose which risk segment to highlight by default:
       * prefer High Risk, then Medium, then No Risk — skipping any with a 0 count.
       * Falls back to "highRisk" if every bucket is empty. */
      const pickDefaultRisk = (subject) => {
        const s = subject?.summary || {};
        // Severity-first: prefer High Risk, fall back to Medium, then No Risk.
        // Falls back to "highRisk" if every bucket is empty.
        if ((s.highRisk ?? 0) > 0) return "highRisk";
        if ((s.mediumRisk ?? 0) > 0) return "mediumRisk";
        if ((s.noRisk ?? 0) > 0) return "noRisk";
        return "highRisk";
      };
      // Auto-select first subject + High Risk on initial load (class teacher only)
      if (mapped.subjects?.length > 0 && !selectedRiskKey) {
        const firstSubject = mapped.subjects[0];
        // Class teacher only — pick first subject (subject teacher has just one, pinned via subject_id)
        if (isClassTeacher && !selectedSubjectId) {
          setSelectedSubjectId(firstSubject.id);
        }
        setSelectedRiskKey(pickDefaultRisk(firstSubject));
      }
    } catch (err) {
      if (!isAbortError(err)) safeSet(setSubjStatus, "error", controller);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    school_id,
    selectedClass?.class_id,
    selectedClass?.section_id,
    selectedClass?.class_staff, // ← refetch if role flag changes
    selectedClass?.subject_id, // ← refetch when subject changes
    user_id,
    academic_id,
    selectedSubjectId,
    selectedRiskKey,
    safeSet,
  ]);

  const fetchExamScheduleList = useCallback(async () => {
    if (!school_id || !selectedClass?.class_id) return;
    examListAbortRef.current?.abort();
    const controller = new AbortController();
    examListAbortRef.current = controller;
    try {
      const response = await AxiosInstance.post(
        `/exam/examSchedule/list`,
        {
          school_id,
          academic_id: academic_id || "",
          config_id: "",
          class_id: selectedClass.class_id,
          section_id: selectedClass.section_id || "",
        },
        { signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      if (response?.data?.status && Array.isArray(response.data.data)) {
        const rawExams = response.data.data;
        if (rawExams.length > 0) {
          const mappedOptions = rawExams.map((item) => ({
            value: String(item.config_id),
            label: item.exam_type || "Unnamed Exam",
          }));
          safeSet(setPeriodOptions, mappedOptions, controller);
          safeSet(setHeatmapPeriod, mappedOptions[0].value, controller);
        } else {
          safeSet(setPeriodOptions, [], controller);
          safeSet(setHeatmapPeriod, "", controller);
          safeSet(
            setHeatmapData,
            { summary: null, students: [], pagination: null },
            controller,
          );
          safeSet(setHeatmapStatus, "success", controller);
        }
      } else {
        safeSet(setPeriodOptions, [], controller);
        safeSet(setHeatmapPeriod, "", controller);
        safeSet(
          setHeatmapData,
          { summary: null, students: [], pagination: null },
          controller,
        );
        safeSet(setHeatmapStatus, "success", controller);
      }
    } catch (err) {
      if (!isAbortError(err)) {
        safeSet(setPeriodOptions, [], controller);
      }
    }
  }, [school_id, selectedClass, academic_id, safeSet]);

  useEffect(() => {
    fetchExamScheduleList();
    return () => examListAbortRef.current?.abort();
  }, [fetchExamScheduleList]);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedHeatmapSearch(heatmapSearch.trim()),
      400,
    );
    return () => clearTimeout(timer);
  }, [heatmapSearch]);

  const loadHeatmap = useCallback(async () => {
    if (
      !school_id ||
      !selectedClass?.class_id ||
      !selectedClass?.section_id ||
      !academic_id ||
      !heatmapPeriod
    )
      return;
    heatmapAbortRef.current?.abort();
    const controller = new AbortController();
    heatmapAbortRef.current = controller;
    safeSet(setHeatmapStatus, "loading", controller);
    try {
      const { data } = await AxiosInstance.post(
        "/dashboard/staff/classPerformanceHeatmap",
        {
          school_id,
          class_id: selectedClass.class_id,
          section_id: selectedClass.section_id,
          academic_id,
          config_id: heatmapPeriod,
          page: heatmapPage,
          limit: heatmapLimit,
          ...(debouncedHeatmapSearch ? { search: debouncedHeatmapSearch } : {}),
        },
        { signal: controller.signal },
      );
      // In loadHeatmap try block, after getting data:
      const mapped = mapHeatmapResponse(data);
      const apiMessage = data?.message || "";
      if (controller.signal.aborted) return;

      safeSet(setHeatmapData, mapped, controller);
      safeSet(setHeatmapMessage, apiMessage, controller);
      safeSet(setHeatmapStatus, "success", controller);
    } catch (err) {
      if (!isAbortError(err)) safeSet(setHeatmapStatus, "error", controller);
    }
  }, [
    school_id,
    selectedClass?.class_id,
    selectedClass?.section_id,
    academic_id,
    heatmapPage,
    heatmapLimit,
    heatmapPeriod,
    debouncedHeatmapSearch,
    safeSet,
  ]);

  useEffect(() => {
    setHeatmapPage(1);
  }, [
    selectedClass?.class_id,
    selectedClass?.section_id,
    heatmapPeriod,
    heatmapLimit,
    debouncedHeatmapSearch,
  ]);

  useEffect(() => {
    loadHeatmap();
    return () => heatmapAbortRef.current?.abort();
  }, [loadHeatmap]);

  /* ==================================================================
   *  Role-scoped trigger — fires only the APIs for the active role
   * ================================================================== */
  useEffect(() => {
    if (!selectedClass) return;

    if (isClassTeacher) {
      fetchDailyAttendance();
      fetchAttendanceRate();
      fetchAttendanceTrend();
      fetchAssessmentProgress();
      // fetchRemedialProgress();
      fetchExamPerformance();
      fetchSyllabusMonth();
      loadAcademicHealthScore();
    } else {
      fetchSyllabusMonth();
      fetchSubjectAssessmentProgress();
      // fetchSubjectRemedialProgress();
      fetchTopicPerformance();
      loadSubjectHealth();
    }

    // Shared (both roles)
    loadAcademicHealth();
    loadSubjectEngagement();

    return () => {
      dailyAbortRef.current?.abort();
      rateAbortRef.current?.abort();
      trendAbortRef.current?.abort();
      assessmentAbortRef.current?.abort();
      remedialAbortRef.current?.abort();
      examAbortRef.current?.abort();
      syllabusAbortRef.current?.abort();
      subjAssessmentAbortRef.current?.abort();
      subjRemedialAbortRef.current?.abort();
      topicAbortRef.current?.abort();
      ahsAbortRef.current?.abort();
      healthScoreAbortRef.current?.abort();
      subjAbortRef.current?.abort();
      subjectHealthAbortRef.current?.abort();
    };
  }, [
    selectedClass,
    isClassTeacher,
    fetchDailyAttendance,
    fetchAttendanceRate,
    fetchAttendanceTrend,
    fetchAssessmentProgress,
    fetchRemedialProgress,
    fetchExamPerformance,
    fetchSyllabusMonth,
    loadAcademicHealthScore,
    fetchSubjectAssessmentProgress,
    // fetchSubjectRemedialProgress,
    fetchTopicPerformance,
    loadAcademicHealth,
    loadSubjectEngagement,
    loadSubjectHealth,
  ]);

  /* ==================================================================
   *  Assessment Trend triggers
   * ================================================================== */
  useEffect(() => {
    if (!selectedClass) return;
    if (isClassTeacher) {
      fetchAssessmentTrendSubjects();
    } else {
      setAtSubjectOptions([]);
      setAtSelectedSubjectId(
        selectedClass.subject_id ? String(selectedClass.subject_id) : "",
      );
    }
    return () => atSubjectsAbortRef.current?.abort();
  }, [selectedClass, isClassTeacher, fetchAssessmentTrendSubjects]);

  // Fetch the stacked bar chart once a subject id is resolved / changed.
  useEffect(() => {
    fetchAssessmentTracker();
    return () => atAbortRef.current?.abort();
  }, [fetchAssessmentTracker]);

  /* ==================================================================
   *  FTR chart triggers
   * ================================================================== */
  useEffect(() => {
    if (!selectedClass) return;
    if (isClassTeacher) {
      fetchFtrSubjects();
    } else {
      setFtrSubjectOptions([]);
      setFtrSelectedSubjectId(
        selectedClass.subject_id ? String(selectedClass.subject_id) : "",
      );
    }
    return () => ftrSubjectsAbortRef.current?.abort();
  }, [selectedClass, isClassTeacher, fetchFtrSubjects]);

  // Fetch FTR data once a subject id is resolved / changed.
  useEffect(() => {
    fetchFtrData();
    return () => ftrAbortRef.current?.abort();
  }, [fetchFtrData]);

  /* ==================================================================
   *  Deferred dropdown handler
   * ================================================================== */
  const handleClassChange = useCallback(
    (e) => {
      const chosen = classOptions.find((opt) => opt.value === e.target.value);
      if (!chosen) return;
      if (e.target.blur) e.target.blur();
      setTimeout(() => {
        if (isMountedRef.current) setSelectedClass(chosen);
      }, 0);
    },
    [classOptions],
  );

  /* ==================================================================
   *  Derived chart data
   * ================================================================== */
  const donutData = useMemo(() => {
    if (attendanceRateData) return attendanceRateData;
    return {
      total: 0,
      segments: [
        {
          key: "present",
          label: t.present || "Present",
          value: 0,
          paletteIndex: 1,
        },
        {
          key: "absent",
          label: t.absent || "Absent",
          value: 0,
          paletteIndex: 5,
        },
        { key: "leave", label: t.leave || "Leave", value: 0, paletteIndex: 3 },
      ],
    };
  }, [attendanceRateData, t]);

  const trendTooltipFormatter = useCallback(
    (idx) => {
      const row = attendanceTrend[idx];

      if (!row) return null;
      return [
        `Present Percentage: ${row.attendance_percent}%`,
        `Present Count: ${row.present_count}`,
        `Absent Count: ${row.absent_count}`,
        `Leave Count: ${row.leave_count}`,
      ];
    },
    [attendanceTrend],
  );
  const trendChartData = useMemo(() => {
    if (!attendanceTrend.length)
      return { labels: [], datasets: [], yMin: 0, yMax: 100, yStepSize: 10 };
    const labels = attendanceTrend.map((e) => e.date);
    const attendanceValues = attendanceTrend.map((e) => e.attendance_percent);
    const { yMin, yMax, yStepSize } = computeYAxis(attendanceValues);
    return {
      labels,
      datasets: [
        {
          label: t.classAttendanceTrend || "Class Attendance Trend",
          data: attendanceValues,
          color: ATTENDANCE_LINE_COLOR,
        },
      ],
      yMin,
      yMax,
      yStepSize,
    };
  }, [attendanceTrend, t]);

  const stackSegmentLabels = useMemo(
    () => ({
      pass: t.passed || "Passed",
      fail: t.failed || "Failed",
      unattended: t.unattended || "Unattended",
    }),
    [t],
  );

  const subjectAssessmentChart = useMemo(
    () => buildSubjectProgressChart(subjAssessmentData),
    [subjAssessmentData],
  );

  const subjectRemedialChart = useMemo(
    () => buildSubjectProgressChart(subjRemedialData),
    [subjRemedialData],
  );

  const topicSegmentLabels = useMemo(
    () => ({
      strong: t.strong || "Strong",
      weak: t.weak || "Weak",
    }),
    [t],
  );

  /* Assessment Trend — fully stacked bar chart only.
   * Two stacked series per assessment:
   *   Fail Count     (red gradient   → --red-dark / --red-light)   ← fail_count
   *   Remedial Count (green gradient → --green-dark / --green-light) ← total_remedial
   * Y axis scales to the tallest stacked (fail + remedial) sum so the
   * full stack always fits. */
  const assessmentTrendChart = useMemo(() => {
    const fails = atPoints.map((p) => p.fail);
    const remedials = atPoints.map((p) => p.totalRemedial);

    // Y-axis max — include fail+remedial stacked sum so the tallest stacked
    // bar always fits.
    const stackedSums = fails.map((f, i) => f + remedials[i]);
    const maxVal = stackedSums.length ? Math.max(...stackedSums) : 0;
    const step =
      maxVal <= 5
        ? 1
        : maxVal <= 10
          ? 1
          : maxVal <= 20
            ? 2
            : maxVal <= 50
              ? 5
              : 10;
    const yMax = maxVal > 0 ? Math.ceil(maxVal / step) * step : 5;

    const datasets = [
      {
        label: t.failCount || "Fail Count",
        data: fails,
        type: "bar",
        stack: "bars", // ← Fail & Remedial stack together
        // Red gradient (dark → light) pulled from app.css via var()
        colorVar: "--red-dark", // resolves legend swatch + border to red
        gradientDarkVar: "--red-dark",
        gradientLightVar: "--red-light",
      },
      {
        label: t.totalRemedial || "Remedial Count",
        data: remedials,
        type: "bar",
        stack: "bars",
        // Green gradient (dark → light) pulled from app.css via var()
        colorVar: "--green-dark", // resolves legend swatch + border to green
        gradientDarkVar: "--green-dark",
        gradientLightVar: "--green-light",
      },
    ];

    return {
      labels:
        atLevel === "assessment"
          ? atPoints.map((p) => p.label) // assessment labels
          : atPoints.map((p) => p.subject), // subject names
      datasets,
      yMin: 0,
      yMax,
      yStepSize: step,
    };
  }, [atPoints, t]);

  /* Assessment Trend — drilldown (remedial) table columns. */
  const atDrilldownColumns = useMemo(
    () => [
      { key: "name", label: t.studentName || "Student Name" },
      {
        key: "totalRemedials",
        label: t.totalRemedials || "Total Remedials",
        align: "center",
      },
      { key: "pass", label: t.pass || "Pass", align: "center" },
      { key: "fail", label: t.fail || "Fail", align: "center" },
      { key: "pending", label: t.unattended || "UnAttended", align: "center" },
      {
        key: "finalStatus",
        label: t.finalStatus || "Final Status",
        align: "center",
        render: (r) => {
          const cleared = (r.finalStatus || "").toLowerCase() === "cleared";
          return (
            <i
              className={
                cleared
                  ? "fa-solid fa-circle-check"
                  : "fa-solid fa-circle-xmark"
              }
              title={r.finalStatus || ""}
              aria-label={r.finalStatus || ""}
              style={{
                fontSize: 20,
                color: cleared ? "#16a34a" : "#dc2626",
              }}
            />
          );
        },
      },
    ],
    [t],
  );

  /* ── FTR chart derived data ── */
  useEffect(() => {
    if (!isClassTeacher && selectedClass?.subject_id) {
      // Subject teacher already has a subject — skip subject level,
      // start directly at assessment level (2 levels instead of 3).
      const subId = String(selectedClass.subject_id);
      const subLabel = selectedClass.subject_name || "";
      setAtLevel("assessment");
      setAtDrillSubject({ id: subId, label: subLabel });
      setFtrLevel("assessment");
      setFtrDrillSubject({ id: subId, label: subLabel });
    } else {
      setAtLevel("subject");
      setAtDrillSubject({ id: "", label: "" });
      setFtrLevel("subject");
      setFtrDrillSubject({ id: "", label: "" });
    }
  }, [selectedClass, isClassTeacher]);
  /* FTR tooltip title: "Assessment N" + "Date: DD-MM-YYYY" */
  const ftrTooltipTitleFormatter = useCallback(
    (dataIndex) => {
      const row = ftrPoints[dataIndex];
      if (!row) return "";
      if (ftrLevel === "subject") return row.subject || row.label;
      return row.createdAt
        ? [row.label, ` ${formatFtrDate(row.createdAt)}`]
        : row.label;
    },
    [ftrPoints, ftrLevel],
  );

  /* FTR tooltip body: only show FTR on bar (dataset 0), suppress line duplicate */
  const ftrTooltipFormatter = useCallback(
    (dataIndex, datasetIndex) => {
      const row = ftrPoints[dataIndex];
      if (!row) return null;
      // Suppress the trend line's duplicate tooltip row
      if (datasetIndex !== 0) return [];
      return [
        `${t.ftr || "FTR"}: ${Number(row.ftrRatio ?? 0).toFixed(2)}`,
        `${t.failCount || "Fail Count"}: ${row.fail}`,
        `${t.totalRemedial || "Remedial Count"}: ${row.totalRemedial}`,
      ];
    },
    [ftrPoints, t],
  );
  console.log(ftrPoints, "ftrPointsftrPointsftrPointsftrPoints");

  /* FTR chart datasets — bar + trend line, both drawing ftr_ratio */
  const ftrChartData = useMemo(() => {
    const ftrValues = ftrPoints.map((p) => Number(p.ftrRatio) || 0);
    const maxFtr = ftrValues.length ? Math.max(...ftrValues) : 0;
    const { max: yMax, step: yStepSize } = ftrNiceScale(maxFtr, 1);

    return {
      labels:
        ftrLevel === "assessment"
          ? ftrPoints.map((p) => p.label)
          : ftrPoints.map((p) => p.subject),
      datasets: [
        {
          label: t.ftr || "FTR",
          type: "bar",
          data: ftrValues,
          gradientDarkVar: "--red-dark",
          gradientLightVar: "--red-light",
          colorVar: "--red-dark",
        },
        {
          label: t.ftrTrend || "FTR Trend",
          type: "line",
          data: ftrValues,
          colorVar: "--violet-dark",
          color: FTR_LINE_COLOR,
          tension: 0.45,
          pointRadius: 4,
        },
      ],
      yMin: 0,
      yMax,
      yStepSize,
    };
  }, [ftrPoints, t]);

  const heatmapColumns = useMemo(() => {
    const subjectCols = (heatmapData?.subjects || []).map((subj) => ({
      key: `subj_${subj.id}`,
      label: subj.name,
      sortAccessor: (r) => r.marks?.[subj.id] ?? 0,
      render: (r) => <MarkCell mark={r.marks?.[subj.id]} />,
    }));
    return [
      {
        key: "name",
        label: t.studentName || "Student Name",
        isName: true,
        sortAccessor: (r) => (r.name || "").toLowerCase(),
      },
      ...subjectCols,
      { key: "totalMark", label: t.total || "Total" },
      {
        key: "rank",
        label: t.rank || "Rank",
        sortAccessor: (r) => (r.rank > 0 ? r.rank : Number.MAX_SAFE_INTEGER),
        render: (r) => (r.rank > 0 ? r.rank : "—"),
      },
    ];
  }, [heatmapData, t]);
  /* ==================================================================
   *  Render
   * ================================================================== */
  const showInitialLoader = subjectsLoading && !selectedClass;
  const showInitialError = !subjectsLoading && !selectedClass && subjectsError;
  const SEGMENT_NOTE =
    t.subjectEngagementNote ||
    "Click on any segment of the chart to view the students included in that category. The student list will be displayed based on the selected segment.";

  const handleClick = (studentId) => {
    navigate("/user/studentCreate", {
      state: { ID: studentId },
    });
  };

  const handleViewAllAssessment = useCallback(
    () => navigate("/user/assessmentList", { state: { activeTab: 0 } }),
    [navigate],
  );

  const handleViewAllRemedial = useCallback(
    () => navigate("/user/assessmentList", { state: { activeTab: 1 } }),
    [navigate],
  );

  /* Assessment Trend — the in-chart subject dropdown (class teacher only). */
  const assessmentTrendSubjectControl = isClassTeacher ? (
    <select
      value={atSelectedSubjectId}
      onChange={(e) => setAtSelectedSubjectId(e.target.value)}
      disabled={atSubjectsLoading || atSubjectOptions.length === 0}
      aria-label={t.selectSubject || "Select subject"}
      style={{
        color: "var(--color-text)",
        padding: "6px 10px",
        borderRadius: 6,
        border: "1px solid var(--color-neutral-400, #ccc)",
        minWidth: 140,
        background: "var(--color-white, #fff)",
      }}
    >
      {atSubjectOptions.length === 0 ? (
        <option value="">
          {atSubjectsLoading
            ? t.loading || "Loading..."
            : t.noSubjects || "No subjects"}
        </option>
      ) : (
        atSubjectOptions.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))
      )}
    </select>
  ) : null;

  /* FTR chart — the in-chart subject dropdown (class teacher only). */
  const ftrSubjectControl = isClassTeacher ? (
    <select
      value={ftrSelectedSubjectId}
      onChange={(e) => setFtrSelectedSubjectId(e.target.value)}
      disabled={ftrSubjectsLoading || ftrSubjectOptions.length === 0}
      aria-label={t.selectSubject || "Select subject"}
      style={{
        color: "var(--color-text)",
        padding: "6px 10px",
        borderRadius: 6,
        border: "1px solid var(--color-neutral-400, #ccc)",
        minWidth: 140,
        background: "var(--color-white, #fff)",
      }}
    >
      {ftrSubjectOptions.length === 0 ? (
        <option value="">
          {ftrSubjectsLoading
            ? t.loading || "Loading..."
            : t.noSubjects || "No subjects"}
        </option>
      ) : (
        ftrSubjectOptions.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))
      )}
    </select>
  ) : null;
  const pickDropdownValue = (opt) =>
    opt && typeof opt === "object" ? (opt.value ?? "") : (opt ?? "");

  /* Build month/year filter options from the academic year's start_date,
   * capped at the earlier of (current month, end_date).
   *   value: "M-YYYY"  (e.g. "4-2026")   label: "Apr 2026" */
  const buildMonthOptions = (startDate, endDate) => {
    if (!startDate) return [];
    const start = new Date(startDate);
    if (Number.isNaN(start.getTime())) return [];

    const now = new Date();
    let upper = now;
    if (endDate) {
      const end = new Date(endDate);
      if (!Number.isNaN(end.getTime()) && end < now) upper = end;
    }

    // Normalise both bounds to the first of their month for clean iteration.
    const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
    const last = new Date(upper.getFullYear(), upper.getMonth(), 1);

    const options = [];
    while (cursor <= last) {
      const m = cursor.getMonth(); // 0-based
      const y = cursor.getFullYear();
      options.push({
        value: `${m + 1}-${y}`,
        // 3-char month + year, no dash, e.g. "Jan 2026".
        label: `${MONTH_NAMES_SHORT[m]} ${y}`,
      });
      cursor.setMonth(cursor.getMonth() + 1);
    }
    return options;
  };
  /* ── Month filter options (shared option list, per-chart selection) ──
   * The "All" option carries a non-empty sentinel value ("all") so the
   * dropdown reliably shows it as the selected default. parseMonthValue()
   * still maps "all" → empty month/year, so the payload is unchanged. */
  const monthOptions = useMemo(
    () => buildMonthOptions(startDate, endDate),
    [startDate, endDate],
  );
  const monthOptionsWithAll = useMemo(
    () => [{ value: "all", label: t.all || "All" }, ...monthOptions],
    [monthOptions, t],
  );

  const studentMonthDropdown = (
    <CustomDropdown
      name="studentMonth"
      value={studentMonth}
      onChange={(opt) => setStudentMonth(pickDropdownValue(opt))}
      options={monthOptionsWithAll}
      placeholder={t.selectMonth || "Select Month"}
      isSearchable={false}
    />
  );
  /* ──────────────────────────────────────────────────────────────
   * Sample fallback — used until the backend returns non-zero data
   * ────────────────────────────────────────────────────────────── */

  const hasMeaningfulSyllabus = (rows) =>
    Array.isArray(rows) &&
    rows.some((r) => (r.overall ?? 0) > 0 || (r.month ?? 0) > 0);

  return (
    <div className={academicStyles.container}>
      <div
        className={dashStyles.headerLine}
        style={{ justifyContent: "space-between" }}
      >
        <div className={dashStyles.leftSection}>
          <h1 className={dashStyles.headerTitle}>
            {" "}
            {isClassTeacher
              ? "Class Teacher Dashboard"
              : "Subject Teacher Dashboard"}{" "}
          </h1>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 8,
            marginBottom: 20,
          }}
        >
          <label style={{ color: "var(--color-text)", fontWeight: 600 }}>
            {t.filterByClass || "Filter by class"}:
          </label>
          {subjectsLoading ? (
            <span style={{ fontSize: 13, color: "var(--text-muted, #888)" }}>
              {t.loading || "Loading..."}
            </span>
          ) : subjectsError ? (
            <span style={{ fontSize: 13, color: "var(--color-error, red)" }}>
              {genericError}
            </span>
          ) : (
            <select
              value={selectedClass ? selectedClass.value : ""}
              onChange={handleClassChange}
              style={{
                color: "var(--color-text)",
                padding: "6px 10px",
                borderRadius: 6,
                border: "1px solid var(--color-neutral-400, #ccc)",
                minWidth: 120,
              }}
            >
              {classOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {showInitialLoader && (
        <div
          className={dashStyles.card}
          style={{ minHeight: 300, position: "relative" }}
        >
          <CustomLoader label={t.loading} />
        </div>
      )}

      {showInitialError && (
        <div
          className={dashStyles.card}
          style={{
            padding: 32,
            textAlign: "center",
            color: "var(--text-muted, #6c757d)",
          }}
        >
          {genericError}
        </div>
      )}

      {selectedClass && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {isClassTeacher && (
            <>
              <div className={dashStyles.row}>
                <div className={`${dashStyles.col} ${dashStyles.col8}`}>
                  <div className={dashStyles.card}>
                    <DailyAttendanceCard
                      summary={attendanceSummary}
                      absentees={absentees}
                      loading={dailyLoading}
                      error={dailyError}
                      handleClick={handleClick}
                    />
                  </div>
                </div>
                <div className={`${dashStyles.col} ${dashStyles.col4}`}>
                  <div className={dashStyles.card}>
                    <CustomDonutChart
                      data={healthData}
                      loading={healthLoading}
                      error={healthError}
                      title={t.academicHealthScore || "Academic Health Score"}
                    />
                    {/* {rateError ? (
                      <p
                        style={{
                          color: "var(--color-error, red)",
                          padding: "1rem",
                        }}
                      >
                        {genericError}
                      </p>
                    ) : (
                      <CustomDonutChart
                        data={donutData}
                        title={t.attendanceRate || "Attendance Rate"}
                        loading={rateLoading}
                      />
                    )} */}
                  </div>
                </div>
              </div>

              <div className={dashStyles.row}>
                <div className={`${dashStyles.col} ${dashStyles.col12}`}>
                  <div className={dashStyles.card}>
                    <LineChart
                      icon={<i className="fa-solid fa-chart-line" />}
                      title={t.classAttendanceTrend || "Class Attendance Trend"}
                      showInfoIcon={false}
                      labels={trendChartData.labels}
                      datasets={trendChartData.datasets}
                      loading={trendLoading}
                      error={trendError}
                      showFilters={false}
                      selectControl={studentMonthDropdown}
                      emptyMessage={
                        t.noAttendanceDataAvailable ||
                        "No attendance data available"
                      }
                      height={320}
                      yMin={trendChartData.yMin}
                      yMax={trendChartData.yMax}
                      yStepSize={trendChartData.yStepSize}
                      yTickSuffix="%" // ← 0 → 0%, 10 → 10%, 20 → 20% …
                      yAxisLabel="Percentage" // ← optional: titles the axis on the left
                      threshold={ATTENDANCE_THRESHOLD}
                      thresholdLabel="Median"
                      showThresholdLegend={true}
                      tooltipFormatter={trendTooltipFormatter}
                    />
                  </div>
                </div>
              </div>

              <div className={dashStyles.row}>
                <div className={`${dashStyles.col} ${dashStyles.col12}`}>
                  <div className={dashStyles.card}>
                    <CustomBarChart
                      stacked
                      segments={TRACKER_SEGMENTS}
                      icon={<i className="fa-solid fa-chart-line" />}
                      title={
                        t.assessmentProgressTracker ||
                        "Assessment Progress Tracker"
                      }
                      data={assessmentProgress}
                      segmentLabels={stackSegmentLabels}
                      loading={assessmentLoading}
                      error={assessmentError}
                      onViewAll={
                        !isClassTeacher ? handleViewAllAssessment : undefined
                      }
                      yAxisLabel="Percentage"
                      viewAllLabel={
                        !isClassTeacher ? t.viewAll || "View All" : undefined
                      }
                    />
                  </div>
                </div>
                {/* <div className={`${dashStyles.col} ${dashStyles.col6}`}>
                  <div className={dashStyles.card}>
                    <CustomBarChart
                      stacked
                      segments={TRACKER_SEGMENTS}
                      icon={<i className="fa-solid fa-chart-line" />}
                      title={
                        t.remedialProgressTracker || "Remedial Progress Tracker"
                      }
                      data={remedialProgress}
                      segmentLabels={stackSegmentLabels}
                      loading={remedialLoading}
                      error={remedialError}
                      onViewAll={
                        !isClassTeacher ? handleViewAllRemedial : undefined
                      }
                      viewAllLabel={
                        !isClassTeacher ? t.viewAll || "View All" : undefined
                      }
                    />
                  </div>
                </div> */}
              </div>
            </>
          )}

          {!isClassTeacher && (
            <>
              <div className={dashStyles.row}>
                <div className={`${dashStyles.col} ${dashStyles.col8}`}>
                  <div className={dashStyles.card}>
                    {/* <SyllabusCompletion
                      icon={<i className="fa-solid fa-book" />}
                      title={t.syllabusCompletion || "Syllabus Completion"}
                      data={subjectSyllabusData}
                      loading={subjectSyllabusLoading}
                      error={subjectSyllabusError}
                      emptyMessage={
                        t.noRecordsToDisplay || "No records to display"
                      }
                    /> */}
                    <SyllabusCompletion
                      title={
                        t.syllabusCompletion || "Syllabus Completion Percentage"
                      }
                      // note={
                      //   t.syllabusCompletionNote ||
                      //   "Left: Overall Completion (%) | Right: Current Month Progress (%)"
                      // }
                      xAxisLabel="Percentage"
                      height="200"
                      data={syllabusData}
                      status={syllabusLoading ? "loading" : "success"}
                      onRetry={fetchSyllabusMonth}
                      emptyMessage={
                        t.noRecordsToDisplay || "No records to display"
                      }
                      errorMessage={syllabusError || genericError}
                      top={"50%"}
                    />
                  </div>
                </div>
                <div className={`${dashStyles.col} ${dashStyles.col4}`}>
                  <div className={dashStyles.card}>
                    <CustomDonutChart
                      data={subjectHealthData}
                      loading={subjectHealthLoading}
                      error={subjectHealthError}
                      title={t.subjectHealthScore || "Subject Health Score"}
                    />
                  </div>
                </div>
              </div>

              <div className={dashStyles.row}>
                <div className={`${dashStyles.col} ${dashStyles.col12}`}>
                  <div className={dashStyles.card}>
                    <HorizontalBarChart
                      title={
                        t.assessmentProgressTracker ||
                        "Assessment Progress Tracker"
                      }
                      icon={<FaChartLine />}
                      labels={subjectAssessmentChart.labels}
                      datasets={subjectAssessmentChart.datasets}
                      loading={subjAssessmentLoading}
                      error={subjAssessmentError}
                      showViewAll
                      xAxisLabel="Percentage"
                      viewAllLabel={t.viewAll || "View All"}
                      onViewAll={() => navigate("/user/assessmentList")}
                      emptyMessage={t.noDataAvailable || "No data available"}
                      height={280}
                    />
                  </div>
                </div>
                {/* <div className={`${dashStyles.col} ${dashStyles.col6}`}>
                  <div className={dashStyles.card}>
                    <HorizontalBarChart
                      title={
                        t.remedialProgressTracker || "Remedial Progress Tracker"
                      }
                      icon={<FaChartLine />}
                      labels={subjectRemedialChart.labels}
                      datasets={subjectRemedialChart.datasets}
                      loading={subjRemedialLoading}
                      error={subjRemedialError}
                      showViewAll
                      viewAllLabel={t.viewAll || "View All"}
                      onViewAll={() => navigate("/user/assessmentList")}
                      emptyMessage={t.noDataAvailable || "No data available"}
                      height={280}
                    />
                  </div>
                </div> */}
              </div>

              <div className={dashStyles.row}>
                <div className={`${dashStyles.col} ${dashStyles.col12}`}>
                  <div className={dashStyles.card}>
                    <CustomBarChart
                      stacked
                      icon={<i className="fa-solid fa-chart-column"></i>}
                      title={
                        t.topicWisePerformance ||
                        "Topic-Wise Performance Insights"
                      }
                      yAxisLabel="Percentage"
                      data={topicData}
                      segmentLabels={topicSegmentLabels}
                      loading={topicLoading}
                      error={topicError}
                      emptyMessage={
                        t.noRecordsToDisplay || "No topic data available"
                      }
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Assessment Trend — shared (fully stacked bar chart; dropdown only for class teacher) */}
          <div className={dashStyles.row}>
            <div className={`${dashStyles.col} ${dashStyles.col12}`}>
              <div className={dashStyles.card}>
                <LineAndBarChart
                  icon={<i className="fa-solid fa-chart-line" />}
                  title={t.assessmentTrend || "Assessment Trend"}
                  labels={assessmentTrendChart.labels}
                  datasets={assessmentTrendChart.datasets}
                  loading={atLoading}
                  error={atError}
                  height={320}
                  tension={0.5}
                  yMin={assessmentTrendChart.yMin}
                  yMax={assessmentTrendChart.yMax}
                  yStepSize={assessmentTrendChart.yStepSize}
                  xAxisLabel={t.assessmentsAxisLabel || "Assessments"}
                  yAxisLabel={t.count || "Count"}
                  emptyMessage={t.noDataAvailable || "No data available"}
                  stackedBars={true}
                  showXTicks={isClassTeacher ? atLevel === "subject" : ""}
                  canGoBack={atLevel === "assessment" && isClassTeacher}
                  onBack={() => {
                    setAtLevel("subject");
                    setAtDrillSubject({ id: "", label: "" });
                  }}
                  {...(atLevel === "subject"
                    ? {
                        barDrilldown: true,
                        onBarClick: (idx) => {
                          const row = atPoints[idx];
                          if (!row?.subjectId) return;
                          setAtDrillSubject({
                            id: row.subjectId,
                            label: row.subject,
                          });
                          setAtLevel("assessment");
                        },
                      }
                    : {
                        onPointClick: fetchAssessmentRemedial,
                        pointDetails: atPoints,
                        drilldownLoading: atDrilldownLoading,
                        drilldownError: atDrilldownError,
                        drilldownColumns: atDrilldownColumns,
                        drilldownTitleSuffix:
                          t.remedialDetailsSuffix || " — Remedial Details",
                        drilldownText: {
                          showing: t.showing || "Showing",
                          to: t.to || "to",
                          of: t.of || "of",
                          entries: t.entries || "entries",
                          previous: t.prev || "Previous",
                          next: t.next || "Next",
                          note: t.noteLabel || "Note:",
                        },
                      })}
                  note={
                    t.assessmentTrendNote ||
                    "Click a subject to view its assessments; click an assessment for student-wise remedial status."
                  }
                  tooltipTitleFormatter={assessmentTrendTooltipTitleFormatter}
                  tooltipFormatter={assessmentTrendTooltipFormatter}
                />
              </div>
            </div>
          </div>

          {/* Remedial Assessment Fail Count Ratio (FTR) — shared, no drilldown */}
          <div className={dashStyles.row}>
            <div className={`${dashStyles.col} ${dashStyles.col12}`}>
              <div className={dashStyles.card}>
                <LineAndBarChart
                  icon={<i className="fa-solid fa-chart-column" />}
                  title={
                    t.remedialFtrInsights ||
                    "Remedial Assessment Fail Count Ratio (FTR)"
                  }
                  labels={ftrChartData.labels}
                  datasets={ftrChartData.datasets}
                  loading={ftrLoading}
                  error={ftrError}
                  height={320}
                  yMin={ftrChartData.yMin}
                  yMax={ftrChartData.yMax}
                  yStepSize={ftrChartData.yStepSize}
                  yAxisLabel={t.ftr || "Failed to Remedial Ratio"}
                  xAxisLabel={t.assessmentsAxisLabel || "Assessments"}
                  barDrilldown={ftrLevel === "subject"}
                  showXTicks={isClassTeacher ? ftrLevel === "subject" : ""}
                  onBack={() => {
                    setFtrLevel("subject");
                    setFtrDrillSubject({ id: "", label: "" });
                  }}
                  canGoBack={ftrLevel === "assessment" && isClassTeacher}
                  onBarClick={(idx) => {
                    if (ftrLevel !== "subject") return;
                    const row = ftrPoints[idx];
                    if (!row?.subjectId) return;
                    setFtrDrillSubject({
                      id: row.subjectId,
                      label: row.subject,
                    });
                    setFtrLevel("assessment");
                  }}
                  emptyMessage={t.noDataAvailable || "No data available"}
                  stackedBars={false}
                  note={
                    t.ftrNote ||
                    "Hover over a bar to see the FTR value for each assessment."
                  }
                  tooltipTitleFormatter={ftrTooltipTitleFormatter}
                  tooltipFormatter={ftrTooltipFormatter}
                />
              </div>
            </div>
          </div>

          {/* Academic Health Insights — shared */}
          <div className={dashStyles.row}>
            <div className={`${dashStyles.col} ${dashStyles.col12}`}>
              <div className={dashStyles.card}>
                <AcademicHealthInsights
                  data={ahsData}
                  status={ahsStatus}
                  onSegmentClick={handleAhsSegmentClick}
                  onRetry={loadAcademicHealth}
                  activeRiskKey={ahsActiveRisk}
                  notePosition="header"
                  handleClick={handleClick}
                  note={SEGMENT_NOTE}
                />
              </div>
            </div>
          </div>

          {/* Subject Engagement — shared, different layouts per role */}
          {isClassTeacher ? (
            <div className={dashStyles.row}>
              <div className={`${dashStyles.col} ${dashStyles.col12}`}>
                <div className={dashStyles.card}>
                  <SubjectEngagementInsights
                    data={subjData}
                    status={subjStatus}
                    note={
                      t.subjectEngagementNote ||
                      "Click on any segment of the chart to view the students included in that category."
                    }
                    selectedSubjectId={selectedSubjectId}
                    selectedRiskKey={selectedRiskKey}
                    onSubjectSegmentClick={handleSubjectSegmentClick}
                    onRetry={loadSubjectEngagement}
                    handleClick={handleClick}
                  />
                </div>
              </div>
            </div>
          ) : (
            // Subject Engagement Risk Analysis
            <div className={dashStyles.row}>
              <div className={`${dashStyles.col} ${dashStyles.col12}`}>
                <div className={dashStyles.card}>
                  <SubjectEngagementInsights
                    layout="split"
                    title={
                      t.subjectEngagementRiskAnalysis ||
                      "Subject Engagement Risk Analysis"
                    }
                    note={
                      t.subjectEngagementNote ||
                      "Click on any segment of the chart to view the students included in that category."
                    }
                    data={subjData}
                    status={subjStatus}
                    selectedRiskKey={selectedRiskKey}
                    onSubjectSegmentClick={(subjectId, riskKey) =>
                      handleSubjectSegmentClick(subjectId, riskKey)
                    }
                    onRetry={loadSubjectEngagement}
                    handleClick={handleClick}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Exam wise Performance + Syllabus Completion — class teacher only */}
          {isClassTeacher && (
            <div className={dashStyles.row}>
              <div className={`${dashStyles.col} ${dashStyles.col6}`}>
                <div className={dashStyles.card}>
                  <ExamPerformanceChart
                    data={performanceData}
                    loading={examLoading}
                    error={examError}
                  />
                </div>
              </div>
              <div className={`${dashStyles.col} ${dashStyles.col6}`}>
                <div className={dashStyles.card}>
                  <SyllabusCompletion
                    title={t.syllabusCompletion || "Syllabus Completion"}
                    // note={
                    //   t.syllabusCompletionNote ||
                    //   "Left: Overall Completion (%) | Right: Current Month Progress (%)"
                    // }
                    xAxisLabel="Percentage"
                    data={syllabusData}
                    status={syllabusLoading ? "loading" : "success"} // ← always success when not loading
                    onRetry={fetchSyllabusMonth}
                    errorMessage={syllabusError || genericError}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Class Performance Heatmap — shared */}
          {/* <div className={dashStyles.row}>
            <div className={`${dashStyles.col} ${dashStyles.col12}`}>
              <div className={dashStyles.card}>
                <ClassPerformanceHeatmap
                  title={
                    t.ClassPerformanceHeatmap || "Class Performance Heatmap"
                  }
                  columns={heatmapColumns}
                  data={heatmapData}
                  searchPlaceholder="Search by student name"
                  status={heatmapStatus}
                  page={heatmapPage}
                  perPage={heatmapLimit}
                  period={heatmapPeriod}
                  search={heatmapSearch}
                  onPageChange={setHeatmapPage}
                  onLimitChange={setHeatmapLimit}
                  periodOptions={periodOptions}
                  onPeriodChange={setHeatmapPeriod}
                  onSearchChange={setHeatmapSearch}
                  onRetry={loadHeatmap}
                  message={heatmapMessage}
                />
              </div>
            </div>
          </div> */}
        </div>
      )}
    </div>
  );
};

export default AiDashboard;
